import { useEffect, useMemo, useRef, useState } from "react";
import { apiChangePassword, apiGetSessionBootstrap, apiListEmpresas, apiListLojas, apiListPermissions, apiLogin, apiLoginWithPin, apiLogout, apiRegisterAppClientVersion, apiRegisterSubscriptionDevice, apiRemoveLoginPin, apiSelectUserStore, apiSetLoginPin, apiSyncAgtPrivateKeyToLocalVault, apiValidateSession, clearCachedBlockedPagesSnapshot, readCachedPermissionCatalog } from "./authApi";
import { cacheOfflineLoginRecord, clearOfflineOperationalContext, ensureOfflineStorageReady, enqueuePendingSync, getOfflineLoginRecordForUser, invalidateOfflineLoginCacheForUser, isNetworkUnavailable, OFFLINE_STORAGE_READY_EVENT, persistOfflineOperationalContext, readOfflineCompanies, readOfflineOperationalContext, readPendingSyncQueue, removePendingSyncItem, tryOfflineLogin, updateOfflinePasswordCacheForUser, writeOfflineCompanies, writePendingSyncQueue } from "./offlineErp";
import { createPosOfflineSyncRunner, POS_OFFLINE_SYNC_INTERVAL_MS } from "./posOfflineSync";
import { createCustomerOfflineSyncRunner } from "./customerOfflineSync";
import { toast } from "./toast";

const CUSTOMER_OFFLINE_SYNC_INTERVAL_MS = 20_000;

export const COMPANIES = [];
const DEFAULT_COMPANY_LOGO = "/assets/images/logoApp.png";
const COMPANY_LOGO_LOCAL_CACHE_PREFIX = "angolabi.erp.companyLogo.local";

const ENV_API_BASE_URL = String(import.meta.env.VITE_API_BASE_URL || "").trim();
const CONTEXT_HOSTNAME = typeof window !== "undefined"
  ? String(window.location.hostname || "").toLowerCase()
  : "";
const IS_TAURI_DESKTOP = Boolean(
  !import.meta.env.DEV
  && typeof window !== "undefined"
  && (CONTEXT_HOSTNAME === "tauri.localhost" || typeof window.__TAURI_INTERNALS__ !== "undefined")
);
const PRODUCTION_API_BASE_URL = "https://dissaax.com/api_erp";
const DEFAULT_LOCAL_API_BASE_URL = "https://dissaax.com/api_erp";
const DEFAULT_DESKTOP_API_BASE_URL = DEFAULT_LOCAL_API_BASE_URL;
const DESKTOP_API_BASE_URL = String(import.meta.env.VITE_API_BASE_URL || "").trim() || DEFAULT_DESKTOP_API_BASE_URL;
const APP_VERSION_URL = `${String(import.meta.env.VITE_API_BASE_URL || (IS_TAURI_DESKTOP ? DEFAULT_DESKTOP_API_BASE_URL : PRODUCTION_API_BASE_URL)).replace(/\/$/, "")}/app_version.php`;
const APP_CLIENT_VERSION_DEVICE_KEY = "angolabi.erp.desktop.deviceId";
const APP_CLIENT_VERSION_HEARTBEAT_KEY = "angolabi.erp.desktop.lastHeartbeatAt";
const APP_CLIENT_VERSION_HEARTBEAT_INTERVAL_MS = 1000 * 60;
const SUBSCRIPTION_DEVICE_ID_KEY = "angolabi.erp.subscription.deviceId";
// Chave antiga (pré multi-conta) — só usada para migração no primeiro acesso.
const LOGIN_PIN_CONFIGURED_KEY = "angolabi.erp.loginPinConfigured";
// Um dispositivo (ex.: POS partilhado) pode ter várias contas com PIN
// configurado — por isso isto é sempre uma lista, nunca um único registo. O
// backend já suportava isto (uq_utilizador_pins_user_device é por par
// utilizador+dispositivo); só o localStorage é que só guardava uma conta de
// cada vez, fazendo com que configurar um segundo PIN "apagasse" o primeiro
// do dispositivo (o registo real no servidor continuava lá, só deixava de
// ser encontrado localmente).
const LOGIN_PIN_PROFILES_KEY = "angolabi.erp.loginPinProfiles";
const LOGIN_PIN_LAST_USED_KEY = "angolabi.erp.loginPinLastUsed";

function normalizePinIdentifier(value) {
  return String(value || "").trim().toLowerCase();
}

function readLoginPinProfilesRaw() {
  if (typeof window === "undefined") return [];

  try {
    const rawList = window.localStorage.getItem(LOGIN_PIN_PROFILES_KEY);
    if (rawList) {
      const parsed = JSON.parse(rawList);
      if (Array.isArray(parsed)) {
        return parsed
          .map((item) => ({
            identifier: String(item?.identifier || "").trim(),
            userName: String(item?.userName || "").trim(),
            savedAt: Number(item?.savedAt) || 0,
          }))
          .filter((item) => item.identifier !== "");
      }
    }

    // Migração de uma única sessão anterior à introdução de múltiplos
    // perfis — sem isto, quem já tinha PIN configurado via o PIN
    // "desaparecer" do dispositivo na próxima visita após esta alteração.
    const legacyRaw = window.localStorage.getItem(LOGIN_PIN_CONFIGURED_KEY);
    if (legacyRaw) {
      const legacy = JSON.parse(legacyRaw);
      const identifier = String(legacy?.identifier || "").trim();
      if (identifier) {
        const migrated = [{
          identifier,
          userName: String(legacy?.userName || "").trim(),
          savedAt: Number(legacy?.savedAt) || Date.now(),
        }];
        window.localStorage.setItem(LOGIN_PIN_PROFILES_KEY, JSON.stringify(migrated));
        window.localStorage.removeItem(LOGIN_PIN_CONFIGURED_KEY);
        return migrated;
      }
    }
  } catch {
    // localStorage indisponível ou corrompido — trata como "sem perfis".
  }

  return [];
}

// Todas as contas com PIN configurado neste dispositivo (para o seletor de
// conta no ecrã de login, quando há mais do que uma).
export function readLoginPinProfiles() {
  return readLoginPinProfilesRaw();
}

// Perfil de UMA conta específica — usado para saber se O UTILIZADOR ACTUAL
// (não "alguém") já tem PIN neste dispositivo, ex. no seu próprio perfil.
export function readLoginPinConfiguredForIdentifier(identifier) {
  const normalized = normalizePinIdentifier(identifier);
  if (!normalized) return null;

  return readLoginPinProfilesRaw().find((profile) => normalizePinIdentifier(profile.identifier) === normalized) || null;
}

export function readLoginPinLastUsedIdentifier() {
  return readLocalStorageValue(LOGIN_PIN_LAST_USED_KEY);
}

export function writeLoginPinLastUsedIdentifier(identifier) {
  const normalized = String(identifier || "").trim();
  if (normalized) {
    writeLocalStorageValue(LOGIN_PIN_LAST_USED_KEY, normalized);
  }
}

function upsertLoginPinProfile({ identifier, userName }) {
  const normalizedIdentifier = String(identifier || "").trim();
  if (!normalizedIdentifier) return;

  const profiles = readLoginPinProfilesRaw().filter(
    (profile) => normalizePinIdentifier(profile.identifier) !== normalizePinIdentifier(normalizedIdentifier)
  );
  profiles.push({ identifier: normalizedIdentifier, userName: String(userName || "").trim(), savedAt: Date.now() });

  try {
    window.localStorage.setItem(LOGIN_PIN_PROFILES_KEY, JSON.stringify(profiles));
  } catch {
    // localStorage indisponível (modo privado, quota) — não é crítico.
  }
  writeLoginPinLastUsedIdentifier(normalizedIdentifier);
}

function removeLoginPinProfile(identifier) {
  const normalized = normalizePinIdentifier(identifier);
  if (!normalized) return;

  const profiles = readLoginPinProfilesRaw().filter(
    (profile) => normalizePinIdentifier(profile.identifier) !== normalized
  );

  try {
    window.localStorage.setItem(LOGIN_PIN_PROFILES_KEY, JSON.stringify(profiles));
  } catch {
    // ignora falhas de persistencia local
  }

  if (normalizePinIdentifier(readLoginPinLastUsedIdentifier()) === normalized) {
    try {
      window.localStorage.removeItem(LOGIN_PIN_LAST_USED_KEY);
    } catch {
      // ignora
    }
  }
}

const LOGIN_METHOD_PREFERENCE_KEY = "angolabi.erp.loginMethodPreference";
const LOGIN_METHOD_PREFERENCE_VALUES = ["pin", "password", "both"];

// Preferência por dispositivo de qual método abre primeiro no login, quando
// já existe PIN configurado neste dispositivo:
//  - "pin"      → abre directamente no ecrã de PIN.
//  - "password" → abre sempre em email/telefone, sem oferecer o atalho de PIN.
//  - "both"     → comportamento actual (default): abre em email/telefone, mas
//                 mostra o atalho para entrar com PIN.
// Sem PIN configurado neste dispositivo, esta preferência não tem efeito —
// só há o formulário de email/telefone.
export function readLoginMethodPreference() {
  if (typeof window === "undefined") return "both";

  try {
    const stored = String(window.localStorage.getItem(LOGIN_METHOD_PREFERENCE_KEY) || "").trim();
    return LOGIN_METHOD_PREFERENCE_VALUES.includes(stored) ? stored : "both";
  } catch {
    return "both";
  }
}

export function writeLoginMethodPreference(value) {
  if (typeof window === "undefined") return;

  const normalized = LOGIN_METHOD_PREFERENCE_VALUES.includes(value) ? value : "both";
  try {
    window.localStorage.setItem(LOGIN_METHOD_PREFERENCE_KEY, normalized);
  } catch {
    // localStorage pode estar indisponível (modo privado, quota) — não é crítico.
  }
}
const SUBSCRIPTION_DEVICE_REG_PREFIX = "angolabi.erp.subscription.deviceReg";
const SUBSCRIPTION_DEVICE_REG_INTERVAL_MS = 1000 * 60 * 15;
const COOKIE_ONLY_AUTH_TOKEN_MARKER = "__cookie_session__";

function normalizeSessionTokenValue(value) {
  const normalized = String(value || "")
    .replace(/^Bearer\s+/i, "")
    .trim();

  if (!normalized) {
    return "";
  }

  const invalidLiterals = ["undefined", "null", "nan", "false", COOKIE_ONLY_AUTH_TOKEN_MARKER];
  if (invalidLiterals.includes(normalized.toLowerCase())) {
    return "";
  }

  return normalized;
}

function resolveSessionTokenFromLoginResult(result) {
  const candidates = [
    result?.session?.token_sessao,
    result?.session?.tokenSessao,
    result?.session?.token,
    result?.token_sessao,
    result?.tokenSessao,
    result?.token,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeSessionTokenValue(candidate);
    if (normalized) {
      return normalized;
    }
  }

  return "";
}

function resolveSessionValidationToken(candidateToken) {
  const normalizedToken = normalizeSessionTokenValue(candidateToken);
  return normalizedToken || COOKIE_ONLY_AUTH_TOKEN_MARKER;
}

function readLocalStorageValue(key) {
  if (typeof window === "undefined") {
    return "";
  }

  try {
    return String(window.localStorage.getItem(key) || "").trim();
  } catch {
    return "";
  }
}

function writeLocalStorageValue(key, value) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(key, String(value || ""));
  } catch {
    // ignore storage failures
  }
}

function ensureDesktopDeviceId() {
  const existing = readLocalStorageValue(APP_CLIENT_VERSION_DEVICE_KEY);
  if (existing) {
    return existing;
  }

  const generated = `desktop-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  writeLocalStorageValue(APP_CLIENT_VERSION_DEVICE_KEY, generated);
  return generated;
}

export function ensureSubscriptionDeviceId() {
  const existing = readLocalStorageValue(SUBSCRIPTION_DEVICE_ID_KEY);
  if (existing) {
    return existing;
  }

  const generated = `subdev-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  writeLocalStorageValue(SUBSCRIPTION_DEVICE_ID_KEY, generated);
  return generated;
}

function hashDeviceFingerprint(rawValue) {
  const input = String(rawValue || "");
  let hash = 2166136261;

  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return `fp-${(hash >>> 0).toString(16)}`;
}

export function resolveRuntimePlatform() {
  if (typeof window === "undefined") {
    return "web";
  }

  const ua = String(window.navigator?.userAgent || "").toLowerCase();
  const platform = String(window.navigator?.platform || "").toLowerCase();

  if (ua.includes("windows") || platform.includes("win")) return "windows";
  if (ua.includes("mac") || platform.includes("mac")) return "macos";
  if (ua.includes("android")) return "android";
  if (ua.includes("iphone") || ua.includes("ipad") || ua.includes("ios")) return "ios";
  if (ua.includes("linux") || platform.includes("linux")) return "linux";
  return "web";
}

function resolveRuntimeDeviceType() {
  if (typeof window === "undefined") {
    return "desktop";
  }

  const ua = String(window.navigator?.userAgent || "").toLowerCase();
  const isTablet = /ipad|tablet/.test(ua);
  const isMobile = /mobile|android|iphone/.test(ua) && !isTablet;

  if (isTablet) return "tablet";
  if (isMobile) return "mobile";

  const host = String(window.location?.hostname || "").toLowerCase();
  if (host === "tauri.localhost" || typeof window.__TAURI_INTERNALS__ !== "undefined") {
    return "desktop";
  }

  return "desktop";
}

function buildSubscriptionDevicePayload(currentUser) {
  if (typeof window === "undefined") {
    return null;
  }

  const deviceId = ensureSubscriptionDeviceId();
  const platform = resolveRuntimePlatform();
  const deviceType = resolveRuntimeDeviceType();
  const hostname = String(window.location?.hostname || "browser").trim() || "browser";
  const language = String(window.navigator?.language || "").trim();
  const platformName = String(window.navigator?.platform || "").trim();
  const userUuid = String(currentUser?.uuid || "").trim();
  const scopeCompanyUuid = String(currentUser?.companyUuid || "").trim();
  const scopeStoreUuid = String(currentUser?.storeUuid || "").trim();
  const screenResolution = `${Number(window.screen?.width || 0)}x${Number(window.screen?.height || 0)}`;
  const timezone = String(Intl.DateTimeFormat().resolvedOptions().timeZone || "").trim();

  const fingerprintSeed = [
    deviceId,
    userUuid,
    scopeCompanyUuid,
    hostname,
    platform,
    deviceType,
    platformName,
    language,
    screenResolution,
    timezone,
  ].join("|");

  return {
    deviceId,
    payload: {
      companyUuid: scopeCompanyUuid,
      storeUuid: scopeStoreUuid,
      deviceName: `${deviceType}-${hostname}`,
      deviceType,
      platform,
      fingerprint: hashDeviceFingerprint(fingerprintSeed),
    },
  };
}

function resolveApiBaseForAssets() {
  const fromEnv = String(ENV_API_BASE_URL || "").trim().replace(/\/+$/, "");
  if (fromEnv) return fromEnv;
  if (!import.meta.env.DEV) return PRODUCTION_API_BASE_URL;
  if (IS_TAURI_DESKTOP) return DESKTOP_API_BASE_URL;
  if (typeof window !== "undefined" && window.location?.origin) {
    return String(window.location.origin).replace(/\/+$/, "") + "/api_erp";
  }
  return "/api_erp";
}

export function resolveCompanyLogoUrl(rawValue) {
  const value = String(rawValue || "").trim();
  if (!value) return "";

  if (/^(data:|blob:|https?:\/\/)/i.test(value)) {
    return value;
  }

  const normalizedPath = value.startsWith("/") ? value : `/${value}`;
  if (normalizedPath.startsWith("/assets/")) {
    return normalizedPath;
  }

  const apiBase = resolveApiBaseForAssets();
  if (normalizedPath.startsWith("/api_erp/")) {
    return `${apiBase}${normalizedPath.replace(/^\/api_erp/i, "")}`;
  }

  return `${apiBase}${normalizedPath}`;
}

function buildCompanyLogoLocalCacheKey(companyUuid) {
  const normalized = String(companyUuid || "").trim();
  return normalized ? `${COMPANY_LOGO_LOCAL_CACHE_PREFIX}.${normalized}` : "";
}

// Cada logotipo em cache é uma data URL base64 — pode facilmente passar de
// 1MB por empresa. Sem isto, um utilizador que já viu/geriu várias empresas
// (ex.: admin_super) acumula um logotipo em cache POR EMPRESA para sempre,
// e o localStorage (por vezes só ~5MB no total em browsers móveis) acaba
// por esgotar-se — foi assim que "angolabi.erp.offline.companies" chegou a
// falhar a gravar a meio do login. Só a empresa activa precisa do logotipo
// disponível offline, por isso remove os das outras antes de gravar o novo.
function evictOtherCompanyLogoLocalCaches(keepCompanyUuid) {
  if (typeof window === "undefined") return;

  const keepKey = buildCompanyLogoLocalCacheKey(keepCompanyUuid);
  const prefix = `${COMPANY_LOGO_LOCAL_CACHE_PREFIX}.`;

  try {
    const staleKeys = Object.keys(window.localStorage).filter(
      (storedKey) => storedKey.startsWith(prefix) && storedKey !== keepKey
    );
    staleKeys.forEach((storedKey) => window.localStorage.removeItem(storedKey));
  } catch {
    // ignore quota/storage errors
  }
}

export function writeCompanyLogoLocalCache(companyUuid, logoDataUrl) {
  if (typeof window === "undefined") return;

  const key = buildCompanyLogoLocalCacheKey(companyUuid);
  if (!key) return;

  const normalized = String(logoDataUrl || "").trim();
  if (!/^data:image\//i.test(normalized)) return;

  try {
    evictOtherCompanyLogoLocalCaches(companyUuid);
    window.localStorage.setItem(key, normalized);
  } catch {
    // ignore quota/storage errors
  }
}

export function readCompanyLogoLocalCache(companyUuid) {
  if (typeof window === "undefined") return "";

  const key = buildCompanyLogoLocalCacheKey(companyUuid);
  if (!key) return "";

  try {
    const raw = String(window.localStorage.getItem(key) || "").trim();
    return /^data:image\//i.test(raw) ? raw : "";
  } catch {
    return "";
  }
}

export const FISCAL_PRESETS = [
  { label: "Q1 2026", value: "q1-2026", range: "Jan – Mar 2026" },
  { label: "Q4 2025", value: "q4-2025", range: "Out – Dez 2025" },
  { label: "Q3 2025", value: "q3-2025", range: "Jul – Set 2025" },
  { label: "Ano 2025", value: "fy-2025", range: "Jan – Dez 2025" },
  { label: "Ano 2024", value: "fy-2024", range: "Jan – Dez 2024" }
];

export const USER_ROLES = [
  { value: "executive", label: "Executivo", color: "executive", access: "Visão consolidada multiempresa" },
  { value: "gerente_geral", label: "Gerente Geral", color: "manager-general", access: "Cockpit transversal com visão de lojas, equipa e resultado" },
  { value: "gerente_loja", label: "Gerente de Loja", color: "manager-store", access: "Operação local com foco em execução da unidade" },
  { value: "gerente", label: "Gerente", color: "manager-store", access: "Perfil legado com gestão multi-loja dentro da mesma empresa" },
  { value: "sub_gerente", label: "Sub-Gerente", color: "manager-store", access: "Perfil de apoio gerencial para módulos futuros" },
  { value: "financial", label: "Gestor Financeiro", color: "financial", access: "Fiscal, tesouraria e auditoria" },
  { value: "tesoureiro", label: "Tesoureiro", color: "financial", access: "Perfil dedicado a tesouraria para módulos futuros" },
  { value: "contabilista", label: "Contabilista", color: "financial", access: "Perfil dedicado à contabilidade para módulos futuros" },
  { value: "sales", label: "Gestor de Vendas", color: "sales", access: "Comercial, CRM e pricing" },
  { value: "secretario", label: "Secretário", color: "sales", access: "Perfil administrativo para módulos futuros" },
  { value: "atendente", label: "Atendente", color: "sales", access: "Atendimento e apoio operacional para módulos futuros" },
  { value: "recepcionista", label: "Recepcionista", color: "sales", access: "Recepção e suporte inicial para módulos futuros" },
  { value: "operations", label: "Operações", color: "operations", access: "Lojas, stock e execução local" },
  { value: "caixa", label: "Caixa / Operador POS", color: "operations", access: "Acesso exclusivo ao terminal POS" }
];

export const ERP_THEMES = [
  { value: "classic", label: "Clássico ERP", helper: "Vermelho institucional com superfícies claras" },
  { value: "midnight", label: "Midnight Ops", helper: "Tema escuro para operação contínua" },
  { value: "ocean", label: "Atlantic Blue", helper: "Azul executivo com visual analítico" },
  { value: "slate", label: "Slate Finance", helper: "Azul acinzentado com contraste suave e corporativo" }
];


export const MODULE_PERMISSIONS = {
  manager: { roles: ["executive", "gerente_geral", "gerente_loja", "gerente"] },
  executive: { roles: ["executive", "financial", "gerente_geral"] },
  sales: { roles: ["executive", "gerente_geral", "gerente_loja", "gerente", "sales"] },
  pos: { roles: ["executive", "gerente_geral", "gerente_loja", "gerente", "sales", "operations", "caixa"] },
  transacoes: { roles: ["executive", "gerente_geral", "gerente_loja", "gerente", "sales", "operations", "caixa"] },
  fiscal: { roles: ["executive", "gerente_geral", "gerente_loja", "gerente", "financial", "operations"] },
  treasury: { roles: ["executive", "gerente_geral", "gerente_loja", "gerente", "financial", "operations"] },
  agt: { roles: ["executive", "gerente_geral", "gerente_loja", "gerente", "financial", "operations"] },
  customers: { roles: ["executive", "gerente_geral", "gerente_loja", "gerente", "financial", "sales", "operations"] },
  products: { roles: ["executive", "gerente_geral", "gerente_loja", "gerente", "financial", "sales", "operations"] }
};

const MODULE_PERMISSION_RESOURCES = {
  manager: ["utilizadores", "rbac", "sessoes", "empresa", "lojas", "sistema", "pagamentos"],
  executive: ["dashboards"],
  sales: ["dashboards", "vendas"],
  pos: ["vendas"],
  transacoes: ["vendas"],
  fiscal: ["fiscal", "faturas", "dados_sensiveis"],
  treasury: ["bancos", "pagamentos", "fiscal"],
  agt: ["fiscal", "faturas", "dados_sensiveis", "sistema"],
  customers: ["vendas"],
  products: ["stock", "produtos", "precos"],
};

const ROUTE_MODULE_ACCESS = [
  { pattern: /^\/executive-kpi-dashboard(?:\/|$)/i, moduleKey: "executive" },
  { pattern: /^\/sales-analytics-dashboard(?:\/|$)/i, moduleKey: "sales" },
  { pattern: /^\/(?:pos-terminal|transacoes|cobrancas|produtos-vendidos|pos-relatorio-diario)(?:\/|$)/i, moduleKey: "pos" },
  { pattern: /^\/financial-compliance-hub(?:\/|$)/i, moduleKey: "fiscal" },
  { pattern: /^\/treasury-dashboard(?:\/|$)/i, moduleKey: "treasury" },
  { pattern: /^\/agt-management(?:\/|$)/i, moduleKey: "agt" },
  { pattern: /^\/customer-registry(?:\/|$)/i, moduleKey: "customers" },
  { pattern: /^\/(?:product-catalog|vendor-management)(?:\/|$)/i, moduleKey: "products" },
  { pattern: /^\/(?:manager-overview|ceo-dashboard|store-manager-dashboard|access-control|settings)(?:\/|$)/i, moduleKey: "manager" },
];

const FULL_ACCESS_ROLE_CODES = new Set(["executive", "gerente_geral", "ceo"]);

export const ACTION_PERMISSIONS = {
  executiveExport: { roles: ["executive", "financial", "gerente_geral"], consolidatedOnly: true },
  executiveDismissAlerts: { roles: ["executive", "financial", "gerente_geral"] },
  salesExport: { roles: ["executive", "gerente_geral", "gerente_loja", "gerente", "sales"], consolidatedOnly: true },
  salesDrilldown: { roles: ["executive", "gerente_geral", "gerente_loja", "gerente", "sales"] },
  fiscalRefresh: { roles: ["executive", "gerente_geral", "gerente_loja", "gerente", "financial"] },
  fiscalExportPdf: { roles: ["executive", "financial"], consolidatedOnly: true },
  fiscalValidateNif: { roles: ["executive", "gerente_geral", "gerente_loja", "gerente", "financial", "operations"] },
  fiscalExportCsv: { roles: ["executive", "financial"] },
  fiscalDownloadDoc: { roles: ["executive", "gerente_geral", "gerente_loja", "gerente", "financial", "operations"] },
  customerExport: { roles: ["executive", "gerente_geral", "gerente", "financial", "sales"], consolidatedOnly: true },
  customerSyncAgt: { roles: ["executive", "financial"] },
  productCreate: { roles: ["executive", "gerente_geral", "gerente_loja", "gerente", "operations"] },
  productDelete: { roles: ["executive", "gerente_geral", "gerente_loja", "gerente", "operations"] }
};

export const ROLE_HOME_PATHS = {
  executive: "/executive-kpi-dashboard",
  gerente_geral: "/ceo-dashboard",
  gerente_loja: "/store-manager-dashboard",
  gerente: "/store-manager-dashboard",
  sub_gerente: "/home-splash",
  financial: "/financial-compliance-hub",
  tesoureiro: "/home-splash",
  contabilista: "/home-splash",
  sales: "/sales-analytics-dashboard",
  secretario: "/home-splash",
  atendente: "/home-splash",
  recepcionista: "/home-splash",
  operations: "/product-catalog",
  caixa: "/pos-terminal",
  afiliado: "/afiliado",
};

const HOME_SPLASH_DEFAULT_ROLES = new Set([
  "executive",
  "financial",
  "gerente_geral",
  "gerente",
  "gerente_loja",
  "sub_gerente",
  "tesoureiro",
  "contabilista",
  "secretario",
  "atendente",
  "recepcionista",
]);

function isStoreLockedManagerRole(roleValue) {
  return normalizeRoleValue(roleValue) === "gerente_loja";
}

function shouldUseHomeSplashAsDefault(roleValue, user = null) {
  if (isSuperAdminRole(user)) {
    return false;
  }

  const normalizedRole = normalizeRoleValue(roleValue);
  if (!normalizedRole) {
    return false;
  }

  return HOME_SPLASH_DEFAULT_ROLES.has(normalizedRole);
}

function resolveCompanyStoresForUser(user, companies = []) {
  if (!user) return [];

  const companyUuid = String(user?.companyUuid || user?.company_uuid || "").trim();
  const companyId = String(user?.companyId || user?.company_code || "").trim();
  const matchedCompany = (companies || []).find((company) => {
    const cUuid = String(company?.uuid || "").trim();
    const cId = String(company?.id || company?.code || "").trim();
    if (companyUuid && cUuid && cUuid === companyUuid) return true;
    if (companyId && cId && cId === companyId) return true;
    return false;
  });

  const stores = Array.isArray(matchedCompany?.stores) ? matchedCompany.stores : [];
  return stores.filter((store) => String(store?.id || "").trim() !== "all");
}

function shouldForceStoreManagerDashboard(user, companies = []) {
  const normalizedRole = normalizeRoleValue(user?.roleValue || user?.role || user?.papel_codigo || "");
  if (normalizedRole !== "gerente_geral") return false;

  const stores = resolveCompanyStoresForUser(user, companies);
  if (stores.length > 0) {
    return stores.length === 1;
  }

  const directCount = Number(user?.companyStoreCount);
  if (Number.isFinite(directCount) && directCount > 0) {
    return directCount === 1;
  }

  return false;
}

export function hasOrganizationScope(user) {
  const companyRef = String(user?.companyUuid || "").trim();
  const storeRef = String(user?.storeUuid || "").trim();
  return Boolean(companyRef && storeRef);
}

function isTrialPlan(planCode) {
  const normalized = String(planCode || "").trim().toLowerCase();
  return normalized === "trial";
}

function parseSubscriptionDate(value) {
  const raw = String(value || "").trim();
  if (!raw) return null;

  // Aceita formatos SQL comuns (YYYY-MM-DD HH:mm:ss) em browsers que
  // n�o fazem parse confi�vel sem o separador "T".
  const normalized = /^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}$/.test(raw)
    ? raw.replace(" ", "T")
    : raw;

  const parsed = new Date(normalized);
  const timestamp = parsed.getTime();
  return Number.isFinite(timestamp) ? parsed : null;
}

function resolveSubscriptionState(planCode, activationDate, expirationDate, explicitIsActive) {
  const normalizedPlan = String(planCode || "").trim().toLowerCase();
  const hasPlan = Boolean(normalizedPlan);
  const startsAt = activationDate?.getTime?.() || NaN;
  const endsAt = expirationDate?.getTime?.() || NaN;
  const hasStart = Number.isFinite(startsAt);
  const hasEnd = Number.isFinite(endsAt);
  const hasDates = hasStart && hasEnd;

  if (typeof explicitIsActive === "boolean") {
    if (!explicitIsActive) return false;

    // Flag positiva do backend com plano v�lido deve permitir acesso,
    // mesmo se a data vier ausente/inconsistente no payload.
    if (hasPlan && !hasDates) return true;
  }

  if (!hasPlan) return false;
  if (!hasDates) {
    const now = Date.now();

    if (hasStart && !hasEnd) {
      return now >= startsAt;
    }

    if (!hasStart && hasEnd) {
      return now <= endsAt;
    }

    if (explicitIsActive === true) {
      return true;
    }

    // Evita falso bloqueio quando backend devolve plano v�lido sem datas.
    return true;
  }

  const now = Date.now();
  return now >= startsAt && now <= endsAt;
}

function isStoreInsideActiveCompany(activeCompany, activeStore) {
  const activeCompanyUuid = String(activeCompany?.uuid || "").trim();
  const activeCompanyId = String(activeCompany?.id || "").trim();
  const storeCompanyUuid = String(activeStore?.companyUuid || "").trim();
  const storeCompanyId = String(activeStore?.companyId || "").trim();

  if (!activeStore) return false;

  if (activeCompanyUuid && storeCompanyUuid) {
    return activeCompanyUuid === storeCompanyUuid;
  }

  if (activeCompanyId && storeCompanyId) {
    return activeCompanyId === storeCompanyId;
  }

  return false;
}

// A regra efetiva considera primeiro a licença da loja ativa (quando existir),
// depois a assinatura da empresa. Isso evita bloqueio indevido entre dispositivos.
function resolveEffectiveSubscriptionSource(activeCompany, activeStore) {
  const activeCompanyUuid = String(activeCompany?.uuid || "").trim();
  const activeStoreCompanyUuid = String(activeStore?.companyUuid || "").trim();
  const isScopedStore = Boolean(
    activeCompanyUuid
    && activeStoreCompanyUuid
    && activeStoreCompanyUuid === activeCompanyUuid
  );

  const hasStoreLicenseRecord = Boolean(
    String(activeStore?.subscriptionPlan || "").trim()
    || String(activeStore?.subscriptionActivatedAt || "").trim()
    || String(activeStore?.subscriptionExpirationAt || "").trim()
    || typeof activeStore?.subscriptionIsActive === "boolean"
  );

  if (isScopedStore && hasStoreLicenseRecord) {
    return {
      planCode: String(activeStore?.subscriptionPlan || "").trim().toLowerCase(),
      activationDate: parseSubscriptionDate(activeStore?.subscriptionActivatedAt),
      expirationDate: parseSubscriptionDate(activeStore?.subscriptionExpirationAt),
      explicitIsActive: typeof activeStore?.subscriptionIsActive === "boolean"
        ? activeStore.subscriptionIsActive
        : null,
    };
  }

  return {
    planCode: String(activeCompany?.subscriptionPlan || "").trim().toLowerCase(),
    activationDate: parseSubscriptionDate(activeCompany?.subscriptionActivatedAt),
    expirationDate: parseSubscriptionDate(activeCompany?.subscriptionExpirationAt),
    explicitIsActive: typeof activeCompany?.subscriptionIsActive === "boolean"
      ? activeCompany.subscriptionIsActive
      : null,
  };
}

export function hasEffectiveActiveSubscription(activeCompany, activeStore) {
  const source = resolveEffectiveSubscriptionSource(activeCompany, activeStore);
  return resolveSubscriptionState(
    source.planCode,
    source.activationDate,
    source.expirationDate,
    source.explicitIsActive,
  );
}

// Código do plano mais recente (loja > empresa, mesma prioridade de
// hasEffectiveActiveSubscription), independentemente de estar activo ou já
// expirado — usado para saber "que plano é que esta empresa/loja já teve"
// mesmo depois de expirar (ex.: SubscriptionLockedPage a oferecer renovação
// do mesmo plano em vez do catálogo completo).
export function resolveEffectiveSubscriptionPlanCode(activeCompany, activeStore) {
  return resolveEffectiveSubscriptionSource(activeCompany, activeStore).planCode;
}

export function resolveRoleHomePath(roleValue, fallbackPath = "", user = null) {
  if (user && isAfiliadoRole(user)) {
    return "/afiliado";
  }

  if (user && !hasOrganizationScope(user) && !isSuperAdminRole(user)) {
    return "/company-setup";
  }

  if (user && isSuperAdminRole(user)) {
    return "/admin-painel";
  }

  const normalizedRole = normalizeRoleValue(roleValue);
  if (shouldUseHomeSplashAsDefault(normalizedRole, user)) {
    return "/home-splash";
  }

  if (isStoreLockedManagerRole(normalizedRole)) {
    return "/store-manager-dashboard";
  }

  if (fallbackPath && fallbackPath !== "/login") {
    return fallbackPath;
  }

  if (user && shouldForceStoreManagerDashboard(user, COMPANIES)) {
    return "/store-manager-dashboard";
  }

  return ROLE_HOME_PATHS?.[normalizedRole] || ROLE_HOME_PATHS.executive;
}

/**
 * Verifica todos os pontos de licenciamento p�s-login e retorna
 * { path, state } com o destino correto do utilizador.
 *
 * Ordem de prioridade:
 *  1. Sem empresa/loja configuradas → /company-setup
 *  2. Assinatura da empresa inativa  → /subscription-plans (reason: subscription-inactive)
 *  3. Licen�a da loja ativa expirada → /subscription-plans (reason: store-license-expired)
 *  4. Caso contrário                  → rota do papel / fallback
 */
export function isPasswordChangeRequired(user) {
  return Boolean(
    user?.passwordChangeRequired
    || String(user?.passwordChangeReason || "").trim().toLowerCase() === "first_login"
    || String(user?.passwordChangeReason || "").trim().toLowerCase() === "password_expired"
    || Boolean(user?.firstLogin)
    || Number(user?.primeiro_login || 0) === 1
  );
}

const PASSWORD_CHANGE_SNOOZE_PREFIX = "angolabi.erp.passwordChangeSnoozed";

function passwordChangeSnoozeKey(userUuid, token) {
  const normalizedUuid = String(userUuid || "").trim();
  const normalizedToken = String(token || "").trim();
  if (!normalizedUuid || !normalizedToken) return "";
  return `${PASSWORD_CHANGE_SNOOZE_PREFIX}:${normalizedUuid}:${normalizedToken}`;
}

// Adia a troca de senha obrigatória só para a sessão atual (chave inclui o
// token da sessão) — ao voltar a autenticar-se, um novo token é emitido e a
// chave antiga deixa de corresponder, pelo que a exigência volta
// automaticamente no próximo login. Não substitui a exigência real: só evita
// que o ProtectedRoute force o utilizador de volta para /alterar-senha nesta
// sessão depois de ele optar por adiar.
export function snoozePasswordChangeForSession(userUuid, token) {
  if (typeof window === "undefined") return;
  const key = passwordChangeSnoozeKey(userUuid, token);
  if (!key) return;
  try {
    window.sessionStorage.setItem(key, "1");
  } catch {
    // sessionStorage pode estar indisponível (modo privado, quota) — não é crítico.
  }
}

export function isPasswordChangeSnoozed(userUuid, token) {
  if (typeof window === "undefined") return false;
  const key = passwordChangeSnoozeKey(userUuid, token);
  if (!key) return false;
  try {
    return window.sessionStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

export function resolvePostLoginPath(user, companies = [], fallbackPath = "") {
  if (!user) {
    return { path: "/company-setup", state: null };
  }

  if (isPasswordChangeRequired(user)) {
    return {
      path: "/alterar-senha",
      state: {
        reason: String(user?.passwordChangeReason || "first_login").trim().toLowerCase() || "first_login",
      },
    };
  }

    // Admin super nunca deve herdar fallback de rota anterior.
  if (isSuperAdminRole(user)) {
    return { path: "/admin-painel", state: null };
  }

  // Afiliado nunca tem empresa/loja nem assinatura — não faz sentido
  // avaliar nada do fluxo de onboarding/assinatura abaixo para este papel.
  if (isAfiliadoRole(user)) {
    return { path: "/afiliado", state: null };
  }

  const hasCompanyScope = Boolean(String(user?.companyUuid || "").trim());
  const hasStoreScope = Boolean(String(user?.storeUuid || "").trim());
  const canCompleteExecutiveOnboarding = isExecutiveOnboardingRole(user);
  const canBypassScope = isSuperAdminRole(user);
  const normalizedRole = normalizeRoleValue(user?.roleValue || user?.role || user?.papel_codigo || "executive");
  const isStoreLockedRole = isStoreLockedManagerRole(normalizedRole);

  if (!hasCompanyScope) {
    if (canBypassScope) {
      return { path: resolveRoleHomePath(user?.roleValue, fallbackPath, user), state: null };
    }
    return { path: "/company-setup", state: null };
  }

  if (!hasStoreScope) {
    if (canBypassScope) {
      return { path: resolveRoleHomePath(user?.roleValue, fallbackPath, user), state: null };
    }

    if (canCompleteExecutiveOnboarding) {
      return { path: "/company-setup", state: { reason: "missing-store-scope" } };
    }

    return {
      path: "/login",
      state: {
        reason: "missing-store-scope",
        notice: "Sem loja atribuida ao seu utilizador. Contacte o seu Gerente para associar uma loja e tente novamente.",
      },
    };
  }

  if (shouldForceStoreManagerDashboard(user, companies) && !shouldUseHomeSplashAsDefault(normalizedRole, user)) {
    return { path: "/store-manager-dashboard", state: null };
  }

  // Derivar empresa e loja ativas com base nos IDs do utilizador
  const activeCompany = (companies || []).find((c) => c?.id === user?.companyId || c?.uuid === user?.companyUuid)
    || companies?.[0]
    || null;

  const activeStore = activeCompany?.stores?.find((s) => s?.id === user?.storeId || s?.uuid === user?.storeUuid)
    || activeCompany?.stores?.[0]
    || null;

  // Evita flash para /subscription-plans no login quando o contexto de empresas/lojas
  // ainda nao foi hidratado no cliente.
  if (!activeCompany) {
    return {
      path: resolveRoleHomePath(normalizedRole, isStoreLockedRole ? "" : fallbackPath, user),
      state: null,
    };
  }

  const hasActiveSubscription = hasEffectiveActiveSubscription(activeCompany, activeStore);

  if (!hasActiveSubscription) {
    const scopedStore = isStoreInsideActiveCompany(activeCompany, activeStore);
    const hasStoreLicenseFlag = scopedStore && typeof activeStore?.subscriptionIsActive === "boolean";

    if (hasStoreLicenseFlag && activeStore?.subscriptionIsActive === false) {
      return {
        path: "/subscription-plans",
        state: {
          reason: "store-license-expired",
          storeName: activeStore?.name || "",
          storeUuid: activeStore?.uuid || "",
        },
      };
    }

    return {
      path: "/subscription-plans",
      state: { reason: "subscription-inactive", companyName: activeCompany?.name || "" },
    };
  }

  // 3. Rota normal do papel
  if (!isStoreLockedRole && fallbackPath && fallbackPath !== "/login") {
    return { path: fallbackPath, state: null };
  }

  return { path: resolveRoleHomePath(normalizedRole, isStoreLockedRole ? "" : fallbackPath, user), state: null };
}

const STORAGE_KEYS = {
  companyId: "angolabi.erp.companyId",
  storeId: "angolabi.erp.storeId",
  fiscalValue: "angolabi.erp.fiscalValue",
  roleValue: "angolabi.erp.roleValue",
  themeValue: "angolabi.erp.themeValue",
  authSession: "angolabi.erp.authSession",
  authNotice: "angolabi.erp.authNotice",
  authToken: "angolabi.erp.authToken",
  splashForceShow: "angolabi.splash.forceShow"
};

const ERP_CONTEXT_EVENT = "angolabi:erp-context";
const ERP_AUTH_EVENT = "angolabi:erp-auth";
const ERP_SESSION_EXPIRED_EVENT = "angolabi:erp-session-expired";

function isDesktopRuntime() {
  return typeof window !== "undefined" && Boolean(window.__TAURI_INTERNALS__);
}

function getPrimarySessionStorage() {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch {
    return window.localStorage;
  }
}

function getFallbackSessionStorage() {
  if (typeof window === "undefined") {
    return null;
  }

  return isDesktopRuntime() ? window.localStorage : null;
}

function readStoredSessionValue(key) {
  const primary = getPrimarySessionStorage();
  const fallback = getFallbackSessionStorage();

  try {
    const primaryValue = primary?.getItem(key);
    if (primaryValue !== null && primaryValue !== undefined && primaryValue !== "") {
      return primaryValue;
    }
  } catch {
    // ignore storage read failure
  }

  try {
    return fallback?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function writeStoredSessionValue(key, value) {
  const normalizedValue = String(value ?? "");
  const primary = getPrimarySessionStorage();
  const fallback = getFallbackSessionStorage();

  try {
    primary?.setItem(key, normalizedValue);
  } catch {
    // ignore storage write failure
  }

  try {
    if (fallback) {
      fallback.setItem(key, normalizedValue);
    } else if (typeof window !== "undefined") {
      window.localStorage.removeItem(key);
    }
  } catch {
    // ignore storage write failure
  }
}

function removeStoredSessionValue(key) {
  const primary = getPrimarySessionStorage();
  const fallback = getFallbackSessionStorage();

  try {
    primary?.removeItem(key);
  } catch {
    // ignore storage remove failure
  }

  try {
    if (fallback) {
      fallback.removeItem(key);
    } else if (typeof window !== "undefined") {
      window.localStorage.removeItem(key);
    }
  } catch {
    // ignore storage remove failure
  }
}

function clearStoredScopeSessionValues({ keepTheme = true } = {}) {
  removeStoredSessionValue(STORAGE_KEYS.companyId);
  removeStoredSessionValue(STORAGE_KEYS.storeId);
  removeStoredSessionValue(STORAGE_KEYS.fiscalValue);
  removeStoredSessionValue(STORAGE_KEYS.roleValue);
  removeStoredSessionValue(STORAGE_KEYS.splashForceShow);
  // Limpar timestamp de �ltima atividade para que uma nova sess�o n�o seja
  // afetada por inatividade registada de uma sess�o anterior.
  if (typeof window !== "undefined") {
    window.localStorage.removeItem("angolabi.erp.lastActivityAt");
  }

  if (!keepTheme) {
    removeStoredSessionValue(STORAGE_KEYS.themeValue);
  }
}

function buildInitials(value) {
  return String(value || "")
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part?.[0] || "")
    .join("")
    .toUpperCase();
}

function getCompanyById(companyId, companies = COMPANIES) {
  return companies?.find((company) => company?.id === companyId) || companies?.[0] || null;
}

function getCompanyByUuid(companyUuid, companies = COMPANIES) {
  return companies?.find((company) => company?.uuid === companyUuid) || null;
}

function normalizeRoleValue(roleValue) {
  const normalized = String(roleValue || "").trim().toLowerCase();
  if (!normalized) return "executive";

  const aliases = {
    executivo: "executive",
    "diretor executivo": "executive",
    "director executivo": "executive",
    ceo: "gerente_geral",
    "gerente geral": "gerente_geral",
    gerentegeral: "gerente_geral",
    admin: "executive",
    admin_super: "executive",
    "admin super": "executive",
    "administrador super": "executive",
    manager: "gerente",
    // O backend (RoleAccess::ALIASES['gerente']) trata supervisor_loja como
    // sinonimo de gerente/gerente_loja/manager — sem este alias, utilizadores
    // com este codigo de papel ficavam bloqueados de TODOS os modulos em
    // MODULE_PERMISSIONS (access-control incluido), porque nenhuma dessas
    // listas continha a string literal "supervisor_loja".
    supervisor_loja: "gerente",
    cashier: "caixa",
    operador_pos: "caixa",
    "operador pos": "caixa",
    financeiro: "financial",
    tesoureiro: "tesoureiro",
    contabilista: "contabilista",
    vendas: "sales",
    secretario: "secretario",
    "secret�rio": "secretario",
    subgerente: "sub_gerente",
    "sub-gerente": "sub_gerente",
    atendente: "atendente",
    recepcionista: "recepcionista",
    estoquista: "operations",
  };

  return aliases[normalized] || normalized;
}

function resolveCanonicalRoleValue(user) {
  const roleFromCode = normalizeRoleValue(
    user?.roleValue || user?.role || user?.papel_codigo || user?.rawRoleCode || ""
  );
  const roleFromName = normalizeRoleValue(
    user?.papel_nome || user?.roleName || user?.title || user?.rawRoleName || ""
  );

  // Prioriza pap�is espec�ficos (ex.: gerente_geral) sobre gen�ricos (executive).
  if (roleFromCode && roleFromCode !== "executive") return roleFromCode;
  if (roleFromName && roleFromName !== "executive") return roleFromName;

  return roleFromCode || roleFromName || "executive";
}

function getRoleByValue(roleValue) {
  const normalizedRole = normalizeRoleValue(roleValue);
  return USER_ROLES?.find((role) => role?.value === normalizedRole) || USER_ROLES?.[0];
}

function getFiscalByValue(fiscalValue) {
  return FISCAL_PRESETS?.find((preset) => preset?.value === fiscalValue) || FISCAL_PRESETS?.[0];
}

function getStoreById(company, storeId) {
  return company?.stores?.find((store) => store?.id === storeId) || company?.stores?.[0] || null;
}

function buildAggregateStore(stores = [], companyId, companyUuid) {
  if ((stores || []).length <= 1) {
    return stores || [];
  }

  return [
    {
      id: "all",
      uuid: null,
      companyId,
      companyUuid,
      name: "Todas as lojas",
      code: "GLOBAL",
      region: "Escopo consolidado",
      segmentUuid: null,
      segmentKey: null,
      segmentName: null,
      requiresFactorTable: (stores || []).some((store) => Boolean(store?.requiresFactorTable)),
      principal: false,
    },
    ...stores,
  ];
}

function getThemeByValue(themeValue) {
  return ERP_THEMES?.find((theme) => theme?.value === themeValue) || ERP_THEMES?.[0];
}

function isManagerRole(roleValue) {
  const normalizedRole = normalizeRoleValue(roleValue);
  // tesoureiro entra aqui a par de executive/gerente_geral — é um papel
  // dedicado a tesouraria, multi-loja por natureza da função (ver
  // canUseCrossStoreScope() abaixo, que também o inclui).
  return ["executive", "gerente_geral", "gerente_loja", "gerente", "tesoureiro"]?.includes(normalizedRole);
}

function canUseCrossStoreScope(user, roleValue) {
  if (isSuperAdminRole(user)) {
    return true;
  }

  // O backend (RoleAccess::canUseCrossStoreScope) trata "executive" como
  // equivalente a gerente_geral/ceo via alias — normalizeRoleValue já
  // colapsa "ceo" em "gerente_geral", mas "executive" fica como o seu
  // próprio valor normalizado, por isso precisa de ser verificado à parte
  // aqui para não ficar preso a uma única loja ao contrário do backend.
  // tesoureiro precisa de poder ver/filtrar uma loja específica ou todas
  // (RoleAccess::canUseCrossStoreScope no backend já o inclui também).
  const normalized = normalizeRoleValue(roleValue);
  return normalized === "gerente_geral" || normalized === "executive" || normalized === "tesoureiro";
}

function isExecutiveOnboardingRole(user) {
  const normalizedRole = normalizeRoleValue(user?.roleValue || user?.role || user?.papel_codigo || "");
  if (normalizedRole === "gerente_geral") {
    return true;
  }

  const normalizedTitle = String(user?.title || user?.papel_nome || user?.roleName || "").trim().toLowerCase();
  return normalizedTitle === "ceo" || normalizedTitle === "gerente geral";
}

function isSuperAdminRole(user) {
  const normalizedRoleCode = String(user?.roleValue || user?.role || user?.papel_codigo || "").trim().toLowerCase();
  const normalizedRawRoleCode = String(user?.rawRoleCode || "").trim().toLowerCase();
  const normalizedTitle = String(user?.title || user?.papel_nome || user?.roleName || "").trim().toLowerCase();
  const normalizedRawRoleName = String(user?.rawRoleName || "").trim().toLowerCase();

  return ["admin_super", "admin super"].includes(normalizedRoleCode)
    || ["admin_super", "admin super"].includes(normalizedRawRoleCode)
    || ["admin super", "administrador super"].includes(normalizedTitle)
    || ["admin super", "administrador super"].includes(normalizedRawRoleName);
}

// Afiliado é um utilizador sem empresa/loja associada (tal como admin_super)
// — por isso precisa do mesmo tipo de atalho para nunca cair no fluxo de
// onboarding de empresa (/company-setup), que pressupõe hasOrganizationScope.
export function isAfiliadoRole(user) {
  const normalizedRoleCode = String(user?.roleValue || user?.role || user?.papel_codigo || "").trim().toLowerCase();
  const normalizedRawRoleCode = String(user?.rawRoleCode || "").trim().toLowerCase();
  return normalizedRoleCode === "afiliado" || normalizedRawRoleCode === "afiliado";
}

function validateUserScopeAtLogin(user) {
  const hasCompanyScope = Boolean(String(user?.companyUuid || "").trim());
  const hasStoreScope = Boolean(String(user?.storeUuid || "").trim());

  // Admin Super, CEO, Gerente Geral e Afiliado podem entrar sem escopo
  // completo — afiliado nunca tem empresa/loja, por desenho (não é staff
  // de nenhuma empresa do ERP).
  if (isSuperAdminRole(user) || isExecutiveOnboardingRole(user) || isAfiliadoRole(user)) {
    return { ok: true };
  }

  // Demais perfis exigem loja atribuída para concluir autenticação.
  if (!hasCompanyScope || !hasStoreScope) {
    return {
      ok: false,
      message: "Login rejeitado: sem loja atribuida ao seu utilizador. Contacte o seu Gerente para associar uma loja.",
    };
  }

  return { ok: true };
}

function findStoreByReference(storeReference, companies = COMPANIES) {
  if (!storeReference) return null;

  for (const company of companies || []) {
    const matchedStore = company?.stores?.find((store) => store?.id === storeReference || store?.uuid === storeReference);
    if (matchedStore) return matchedStore;
  }

  return null;
}

function matchesScopeByUuid(record, scope, companies = COMPANIES) {
  if (!record) return false;

  const activeCompanyId = scope?.activeCompany?.id || scope?.companyId || null;
  const activeCompanyUuid = scope?.activeCompany?.uuid || scope?.companyUuid || scope?.currentUser?.companyUuid || null;
  const activeStoreId = scope?.activeStore?.id || scope?.storeId || null;
  const activeStoreUuid = scope?.activeStore?.uuid || scope?.storeUuid || scope?.currentUser?.storeUuid || null;

  const recordCompanyId = record?.companyId || record?.company_code || null;
  const recordCompanyUuid = record?.companyUuid || record?.company_uuid || record?.uuid_empresa || getCompanyById(recordCompanyId, companies)?.uuid || null;
  const recordStoreId = record?.storeId || record?.store_code || null;
  const recordStoreUuid = record?.storeUuid || record?.store_uuid || record?.uuid_lojas || findStoreByReference(recordStoreId, companies)?.uuid || null;

  const companyMatches = activeCompanyUuid && recordCompanyUuid
    ? recordCompanyUuid === activeCompanyUuid
    : activeCompanyId
      ? recordCompanyId === activeCompanyId
      : true;

  if (!companyMatches) return false;

  if (activeStoreId === "all") return true;

  return activeStoreUuid && recordStoreUuid
    ? recordStoreUuid === activeStoreUuid
    : activeStoreId
      ? recordStoreId === activeStoreId
      : true;
}

function normalizeCompanyRecord(company) {
  const resolvedLogoUrl = resolveCompanyLogoUrl(company?.url_logo || company?.logo_empresa_url || DEFAULT_COMPANY_LOGO)
    || DEFAULT_COMPANY_LOGO;

  return {
    id: company?.codigo || company?.uuid || null,
    code: company?.codigo || null,
    uuid: company?.uuid || null,
    name: company?.nome_empresa || "Empresa",
    initials: buildInitials(company?.nome_empresa),
    sector: company?.nome_regime_fiscal || company?.cidade || "Operacao",
    regimeUuid: company?.uuid_regime_fiscal || null,
    regimeCode: company?.codigo_regime || null,
    regimeName: company?.descricao_regime_fiscal || company?.nome_regime_fiscal || null,
    kpi: null,
    onlineUsers: 0,
    totalUsers: 0,
    pendingApprovals: 0,
    city: company?.cidade || null,
    municipality: company?.municipio || null,
    country: company?.pais || null,
    nif: company?.nif_empresa || company?.nif || null,
    email: company?.email || null,
    phone: company?.telefone || null,
    website: company?.website || null,
    address: company?.endereco || null,
    indicatorNaoResidente: typeof company?.indicadorNaoResidente !== "undefined" && company?.indicadorNaoResidente !== null
      ? Number(company?.indicadorNaoResidente)
      : null,
    estadoContribuinte: company?.estadoContribuinte || null,
    tipoContribuinte: company?.tipoContribuinte || null,
    establishmentNumber: company?.establishmentNumber || null,
    textoRodapeFatura: company?.texto_rodape_fatura || null,
    qrVerificationMode: String(company?.qr_verificacao_modo || "agt").trim().toLowerCase() === "interno" ? "interno" : "agt",
    subscriptionPlan: company?.plano_assinatura || null,
    subscriptionActivatedAt: company?.data_ativacao_assinatura || company?.ativacao_assinatura || null,
    subscriptionExpirationAt: company?.data_expiracao_assinatura || null,
    subscriptionIsActive: typeof company?.assinatura_ativa !== "undefined" ? Boolean(Number(company?.assinatura_ativa)) : null,
    // Restrição de acesso por incumprimento de compromisso de pagamento
    // (Admin > Planos) — mecanismo independente da assinatura expirar; ver
    // ProtectedRoute em Routes.jsx (bloqueio total, sem excepção de rotas
    // de leitura, ao contrário do fluxo de assinatura expirada).
    paymentRestricted: Boolean(Number(company?.pagamento_restrito ?? 0)),
    paymentRestrictionReason: company?.restricao_pagamento_motivo || null,
    paymentCommitmentDate: company?.restricao_pagamento_data_limite || null,
      licensedStoreCount: Math.max(0, Number(company?.licenciamento_qtd_lojas || 0)),
    billingType: String(company?.tipo_faturacacao || company?.billingType || 'SAFT').trim().toUpperCase(),
    url_logo: resolvedLogoUrl,
    logo_empresa_url: resolvedLogoUrl,
    showDocumentCode: !["0", "false", "nao", "n�o", "no"].includes(String(company?.exibir_codigo_no_documento ?? company?.showDocumentCode ?? 1).trim().toLowerCase()),
    exibir_codigo_no_documento: Number(company?.exibir_codigo_no_documento ?? company?.showDocumentCode ?? 1),
    showRetentionInTaxSummary: !["0", "false", "nao", "n�o", "no"].includes(String(company?.exibir_retencao_noResumo_imposto ?? company?.showRetentionInTaxSummary ?? 1).trim().toLowerCase()),
    exibir_retencao_noResumo_imposto: Number(company?.exibir_retencao_noResumo_imposto ?? company?.showRetentionInTaxSummary ?? 1),
    showRetention: !["0", "false", "nao", "n�o", "no"].includes(String(company?.exibir_retencao ?? company?.showRetention ?? company?.exibir_retencao_noTotalDocumento ?? company?.showRetentionInDocumentTotal ?? 1).trim().toLowerCase()),
    exibir_retencao: Number(company?.exibir_retencao ?? company?.showRetention ?? company?.exibir_retencao_noTotalDocumento ?? company?.showRetentionInDocumentTotal ?? 1),
    showRetentionInDocumentTotal: !["0", "false", "nao", "n�o", "no"].includes(String(company?.exibir_retencao_noTotalDocumento ?? company?.showRetentionInDocumentTotal ?? 1).trim().toLowerCase()),
    exibir_retencao_noTotalDocumento: Number(company?.exibir_retencao_noTotalDocumento ?? company?.showRetentionInDocumentTotal ?? 1),
    canRemoveKey: Boolean(Number(company?.remover_chave ?? company?.canRemoveKey ?? 0)),
    remover_chave: Number(company?.remover_chave ?? company?.canRemoveKey ?? 0),
    showPosCoordinates: !["0", "false", "nao", "n�o", "no"].includes(String(company?.mostrar_cordenadas_POS ?? company?.mostrar_coordenadas_POS ?? company?.mostrar_cordenadas_pos ?? company?.mostrar_coordenadas_pos ?? company?.showPosCoordinates ?? 0).trim().toLowerCase()),
    mostrar_cordenadas_POS: Number(company?.mostrar_cordenadas_POS ?? company?.mostrar_coordenadas_POS ?? company?.mostrar_cordenadas_pos ?? company?.mostrar_coordenadas_pos ?? company?.showPosCoordinates ?? 0),
    autoSuggestCnbs: !["0", "false", "nao", "não", "no"].includes(String(company?.sugestao_automatica_cnbs ?? company?.autoSuggestCnbs ?? 0).trim().toLowerCase()),
    sugestao_automatica_cnbs: Number(company?.sugestao_automatica_cnbs ?? company?.autoSuggestCnbs ?? 0),
    stores: [],
  };
}

function normalizeStoreRecord(store, companyId) {
  const rawStoreLicenseActive = store?.licenca_loja_ativa;
  const hasStoreLicenseRecord = Boolean(
    String(store?.licenca_loja_plano_codigo || "").trim()
    || String(store?.licenca_loja_validade_inicio || "").trim()
    || String(store?.licenca_loja_validade_fim || "").trim()
  );

  return {
    id: store?.codigo || store?.uuid || null,
    uuid: store?.uuid || null,
    companyId,
    companyUuid: store?.uuid_empresa,
    name: store?.nome || "Loja",
    shortName: store?.abreviatura || store?.sigla || null,
    code: String(store?.codigo || "").toUpperCase(),
    region: store?.cidade || "Sem regiao",
    city: store?.cidade || null,
    phone: store?.telefone || null,
    email: store?.email || null,
    address: store?.endereco || store?.morada || store?.endereco_completo || null,
    segmentUuid: store?.uuid_segmento || null,
    segmentKey: store?.codigo_segmento || null,
    segmentName: store?.nome_segmento || null,
    // "Setor" é o ramo de negócio da loja (Farmácia, Armazém, Advocacia...),
    // distinto de "segmento" acima — ver `sectores` / `20260621_sectores_lojas_expansao.sql`.
    sectorUuid: store?.uuid_setor || null,
    sectorKey: store?.codigo_setor || null,
    sectorName: store?.nome_setor || null,
    // "Tipo de Ponto" (Loja/Sucursal/Escritório/.../Outro, ver Definições >
    // Lojas e Pontos) — campo livre onde tipos não previstos na lista (ex.:
    // "Farmácia") também são gravados. É este o campo usado para decidir se
    // uma loja é uma farmácia, não o "segmento" nem o "setor" acima.
    pointType: store?.tipo_ponto || null,
    requiresFactorTable: typeof store?.usa_tabela_factor !== "undefined" ? Boolean(Number(store?.usa_tabela_factor || 0)) : false,
    principal: Boolean(Number(store?.principal || 0)),
    subscriptionIsActive: hasStoreLicenseRecord ? Boolean(Number(rawStoreLicenseActive)) : null,
    subscriptionActivatedAt: store?.licenca_loja_validade_inicio || null,
    subscriptionExpirationAt: store?.licenca_loja_validade_fim || null,
    subscriptionPlan: store?.licenca_loja_plano_codigo || null,
    // Preferências desta loja sobre se a retenção na fonte é subtraída do
    // "Total" do documento em FT/PP/OR e/ou FR/RG (ver Definições > Lojas >
    // Preferências da Loja / a4*PdfService.js, payload.retentionIsDisplayOnly).
    retentionSubtractsInFt: Number(store?.retencao_ft_subtrai_total ?? 0) === 1,
    retentionSubtractsInFrRg: Number(store?.retencao_fr_rg_subtrai_total ?? 1) === 1,
  };
}

function mergeCompanyStores(companies, companyId, stores) {
  return companies?.map((company) => {
    if (company?.id !== companyId) return company;

    const normalizedStores = Array.isArray(stores) ? stores : [];

    return {
      ...company,
      stores: buildAggregateStore(normalizedStores, companyId, company?.uuid),
    };
  });
}

function sanitizeUser(user, companies = COMPANIES) {
  if (!user) return null;

  const normalizeToggleFlag = (value) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value !== 0;
    const normalized = String(value ?? "").trim().toLowerCase();
    if (!normalized || ["0", "false", "nao", "n�o", "no", "off", "null", "undefined"].includes(normalized)) {
      return false;
    }
    return true;
  };

  const normalizePermissionSource = (value) => {
    if (Array.isArray(value)) {
      return value;
    }

    if (typeof value === "string") {
      return value.split(",");
    }

    return [];
  };

  const normalizedRole = resolveCanonicalRoleValue(user);
  const normalizedRoleLabel = getRoleByValue(normalizedRole)?.label;
  const normalizedName = user?.name || user?.nome_completo || "Utilizador";
  const normalizedCompany = user?.companyId || user?.company_code || getCompanyByUuid(user?.companyUuid || user?.company_uuid || null, companies)?.id || null;
  const resolvedCompany = getCompanyById(normalizedCompany, companies);
  const requestedStoreId = user?.storeId || user?.store_code || findStoreByReference(user?.storeUuid || user?.store_uuid, companies)?.id || null;
  const resolvedStore = getStoreById(resolvedCompany, requestedStoreId);
  const storedCompanyStoreCount = Number(user?.companyStoreCount);
  const companyStoreCount = Number.isFinite(storedCompanyStoreCount) && storedCompanyStoreCount > 0
    ? storedCompanyStoreCount
    : (Array.isArray(resolvedCompany?.stores)
      ? resolvedCompany.stores.filter((store) => String(store?.id || "").trim() !== "all").length
      : 0);
  const isStoreLockedRole = isStoreLockedManagerRole(normalizedRole);
  const scopedFallbackStore = (resolvedCompany?.stores || []).find((store) => String(store?.id || "").trim() !== "all") || null;
  const resolvedStoreId = isStoreLockedRole
    ? (requestedStoreId && requestedStoreId !== "all"
      ? requestedStoreId
      : resolvedStore?.id && resolvedStore?.id !== "all"
        ? resolvedStore.id
        : scopedFallbackStore?.id || null)
    : (requestedStoreId || resolvedStore?.id || resolvedCompany?.stores?.find((store) => store?.principal)?.id || resolvedCompany?.stores?.[0]?.id || null);
  const storeFromResolvedId = resolvedStoreId
    ? (resolvedCompany?.stores || []).find((store) => store?.id === resolvedStoreId) || null
    : null;

  const initials = user?.initials || user?.iniciais || buildInitials(normalizedName);
  const permissionCodes = Array.from(new Set([
    ...normalizePermissionSource(user?.permissionCodes),
    ...normalizePermissionSource(user?.permission_codes),
    ...normalizePermissionSource(user?.rolePermissionCodes),
    ...normalizePermissionSource(user?.directPermissionCodes),
    ...normalizePermissionSource(user?.role_permission_codes),
    ...normalizePermissionSource(user?.direct_permission_codes),
  ].map((value) => String(value || "").trim().toLowerCase()).filter(Boolean)));

  return {
    id: user?.id || user?.uuid,
    dbId: user?.dbId || user?.id || null,
    uuid: user?.uuid || null,
    name: normalizedName,
    initials,
    avatarUrl: user?.foto_perfil_url || user?.avatarUrl || null,
    email: user?.email,
    role: String(user?.papel_codigo || user?.role || user?.rawRoleCode || "").trim().toLowerCase() || undefined,
    roleValue: normalizedRole,
    rawRoleCode: String(user?.papel_codigo || user?.role || user?.rawRoleCode || user?.roleValue || "").trim().toLowerCase(),
    rawRoleName: String(user?.papel_nome || user?.roleName || user?.rawRoleName || user?.title || "").trim().toLowerCase(),
    companyId: normalizedCompany,
    companyUuid: user?.companyUuid || user?.company_uuid || resolvedCompany?.uuid || null,
    companyName: user?.companyName || user?.company_name || resolvedCompany?.name || null,
    storeId: resolvedStoreId,
    storeUuid: storeFromResolvedId?.uuid || user?.storeUuid || user?.store_uuid || resolvedStore?.uuid || null,
    storeName: storeFromResolvedId?.name || user?.storeName || user?.store_name || resolvedStore?.name || null,
    companyStoreCount,
    // Evita conflito visual entre labels legados (title) e o c�digo de papel real da sess�o.
    title: normalizedRoleLabel || user?.title || "Executivo",
    permissionCodes,
    alteraPrecoPos: normalizeToggleFlag(user?.alteraPrecoPos ?? user?.altera_preco_pos),
    aplicarDescontoPos: normalizeToggleFlag(user?.aplicarDescontoPos ?? user?.aplicar_desconto_pos),
    retificarCancelarFatura: normalizeToggleFlag(user?.retificarCancelarFatura ?? user?.retificar_cancelar_fatura),
    // Antes este campo nao era exposto aqui: pos-sale-success e o auto-perfil
    // em profile-settings liam currentUser?.impresao_padrao diretamente do
    // objeto sanitizado, que nunca o continha — o tipo de impressao
    // predefinido do utilizador nunca era realmente aplicado. O default (sem
    // valor) e A4 (true), espelhando o DEFAULT 1 da coluna na BD.
    impresaoPadrao: (() => {
      const rawPrintDefault = user?.impresaoPadrao ?? user?.impresao_padrao ?? user?.impressao_padrao;
      return rawPrintDefault === undefined || rawPrintDefault === null
        ? true
        : normalizeToggleFlag(rawPrintDefault);
    })(),
    passwordChangeRequired: Boolean(user?.passwordChangeRequired ?? user?.password_change_required ?? false),
    passwordChangeReason: user?.passwordChangeReason || user?.password_change_reason || null,
    firstLogin: Boolean(user?.primeiro_login ?? false),
    passwordExpiresAt: user?.senha_expira_em || null,
  };
}

function hasAccess(rule, roleValue, isConsolidated) {
  if (!rule) return false;
  const roleAllowed = rule?.roles?.includes(normalizeRoleValue(roleValue));
  if (!roleAllowed) return false;
  if (rule?.consolidatedOnly && !isConsolidated) return false;
  return true;
}

function resolveAccessibleResources(permissionCodes = [], permissionCatalog = []) {
  if (!Array.isArray(permissionCodes) || permissionCodes.length === 0 || !Array.isArray(permissionCatalog) || permissionCatalog.length === 0) {
    return new Set();
  }

  const normalizedCodes = new Set(permissionCodes.map((value) => String(value || "").trim().toLowerCase()).filter(Boolean));
  const resources = new Set();

  permissionCatalog.forEach((permission) => {
    const code = String(permission?.codigo_permissao || "").trim().toLowerCase();
    const resource = String(permission?.modulo_chave || "").trim().toLowerCase();
    if (code && resource && normalizedCodes.has(code)) {
      resources.add(resource);
    }
  });

  return resources;
}

function buildAccessibleModuleSet({ roleValue, isConsolidated, permissionCodes = [], permissionCatalog = [] }) {
  const baseAllowedModules = Object.keys(MODULE_PERMISSIONS).filter((moduleKey) => hasAccess(MODULE_PERMISSIONS?.[moduleKey], roleValue, isConsolidated));
  const normalizedRole = normalizeRoleValue(roleValue);

  if (FULL_ACCESS_ROLE_CODES.has(normalizedRole)) {
    return new Set(baseAllowedModules);
  }

  if (!Array.isArray(permissionCodes) || permissionCodes.length === 0) {
    return new Set(baseAllowedModules);
  }

  const accessibleResources = resolveAccessibleResources(permissionCodes, permissionCatalog);
  if (accessibleResources.size === 0) {
    return new Set(baseAllowedModules);
  }

  const nextModules = new Set();
  baseAllowedModules.forEach((moduleKey) => {
    const requiredResources = MODULE_PERMISSION_RESOURCES?.[moduleKey] || [];
    if (requiredResources.length === 0) {
      nextModules.add(moduleKey);
      return;
    }

    if (requiredResources.some((resource) => accessibleResources.has(String(resource || "").trim().toLowerCase()))) {
      nextModules.add(moduleKey);
    }
  });

  return nextModules.size > 0 ? nextModules : new Set(baseAllowedModules);
}

function resolveModuleKeyForPath(pathname) {
  const normalizedPath = String(pathname || "").trim();
  if (!normalizedPath) {
    return null;
  }

  const match = ROUTE_MODULE_ACCESS.find((entry) => entry.pattern.test(normalizedPath));
  return match?.moduleKey || null;
}

function readSnapshot(companies = COMPANIES) {
  if (typeof window === "undefined") {
    const company = companies?.[0];
    return {
      companyId: company?.id,
      storeId: company?.stores?.[0]?.id,
      fiscalValue: FISCAL_PRESETS?.[0]?.value,
      roleValue: USER_ROLES?.[0]?.value,
      themeValue: ERP_THEMES?.[0]?.value
    };
  }

  const company = getCompanyById(window.localStorage.getItem(STORAGE_KEYS.companyId), companies);
  const store = getStoreById(company, window.localStorage.getItem(STORAGE_KEYS.storeId));
  const fiscal = getFiscalByValue(window.localStorage.getItem(STORAGE_KEYS.fiscalValue));
  const role = getRoleByValue(window.localStorage.getItem(STORAGE_KEYS.roleValue));
  const theme = getThemeByValue(window.localStorage.getItem(STORAGE_KEYS.themeValue));

  return {
    companyId: company?.id,
    storeId: store?.id,
    fiscalValue: fiscal?.value,
    roleValue: role?.value,
    themeValue: theme?.value
  };
}

function persistSnapshot(snapshot) {
  if (typeof window === "undefined") return;

  const nextCompanyId = String(snapshot?.companyId || "").trim();
  const nextStoreId = String(snapshot?.storeId || "").trim();
  const nextFiscalValue = String(snapshot?.fiscalValue || "").trim();
  const nextRoleValue = String(snapshot?.roleValue || "").trim();
  const nextThemeValue = String(snapshot?.themeValue || "").trim();

  const prevCompanyId = String(window.localStorage.getItem(STORAGE_KEYS.companyId) || "").trim();
  const prevStoreId = String(window.localStorage.getItem(STORAGE_KEYS.storeId) || "").trim();
  const prevFiscalValue = String(window.localStorage.getItem(STORAGE_KEYS.fiscalValue) || "").trim();
  const prevRoleValue = String(window.localStorage.getItem(STORAGE_KEYS.roleValue) || "").trim();
  const prevThemeValue = String(window.localStorage.getItem(STORAGE_KEYS.themeValue) || "").trim();

  const isUnchanged =
    prevCompanyId === nextCompanyId
    && prevStoreId === nextStoreId
    && prevFiscalValue === nextFiscalValue
    && prevRoleValue === nextRoleValue
    && prevThemeValue === nextThemeValue;

  if (isUnchanged) {
    return;
  }

  if (nextCompanyId) window.localStorage.setItem(STORAGE_KEYS.companyId, nextCompanyId);
  else window.localStorage.removeItem(STORAGE_KEYS.companyId);

  if (nextStoreId) window.localStorage.setItem(STORAGE_KEYS.storeId, nextStoreId);
  else window.localStorage.removeItem(STORAGE_KEYS.storeId);

  if (nextFiscalValue) window.localStorage.setItem(STORAGE_KEYS.fiscalValue, nextFiscalValue);
  else window.localStorage.removeItem(STORAGE_KEYS.fiscalValue);

  if (nextRoleValue) window.localStorage.setItem(STORAGE_KEYS.roleValue, nextRoleValue);
  else window.localStorage.removeItem(STORAGE_KEYS.roleValue);

  if (nextThemeValue) window.localStorage.setItem(STORAGE_KEYS.themeValue, nextThemeValue);
  else window.localStorage.removeItem(STORAGE_KEYS.themeValue);

  window.dispatchEvent(new CustomEvent(ERP_CONTEXT_EVENT, {
    detail: {
      companyId: nextCompanyId || undefined,
      storeId: nextStoreId || undefined,
      fiscalValue: nextFiscalValue || undefined,
      roleValue: nextRoleValue || undefined,
      themeValue: nextThemeValue || undefined,
    },
  }));
}

function readAuthSession() {
  if (typeof window === "undefined") {
    return {
      isAuthenticated: false,
      currentUser: null,
      token: "",
      sessionId: null,
      sessionUuid: null,
      lastLoginAt: null,
      notice: ""
    };
  }

  try {
    const sessionRaw = window.sessionStorage.getItem(STORAGE_KEYS.authSession)
      || readStoredSessionValue(STORAGE_KEYS.authSession)
      || "null";
    const rawSession = JSON.parse(sessionRaw);
    const rawToken = String(rawSession?.token || "").trim();
    const sessionMode = String(rawSession?.sessionMode || "").trim().toLowerCase();
    const token = rawToken || (sessionMode === "offline" ? "offline-session" : "");
    return {
      isAuthenticated: Boolean(rawSession?.isAuthenticated && rawSession?.currentUser),
      currentUser: sanitizeUser(rawSession?.currentUser),
      token,
      sessionId: rawSession?.sessionId || null,
      sessionUuid: rawSession?.sessionUuid || null,
      lastLoginAt: rawSession?.lastLoginAt || null,
      notice: readStoredSessionValue(STORAGE_KEYS.authNotice) || ""
    };
  } catch {
    return {
      isAuthenticated: false,
      currentUser: null,
      token: "",
      sessionId: null,
      sessionUuid: null,
      lastLoginAt: null,
      notice: readStoredSessionValue(STORAGE_KEYS.authNotice) || ""
    };
  }
}

function persistAuthSession(session) {
  if (typeof window === "undefined") return;

  const safeToken = String(session?.token || "").trim();
  const sessionMode = isOfflineSessionToken(safeToken)
    ? "offline"
    : (safeToken ? "token" : "cookie");

  if (!session?.isAuthenticated || !session?.currentUser?.id) {
    try {
      window.sessionStorage.removeItem(STORAGE_KEYS.authSession);
    } catch {
      // ignore storage remove failure
    }

    removeStoredSessionValue(STORAGE_KEYS.authSession);
    removeStoredSessionValue(STORAGE_KEYS.authToken);
  } else {
    const serializedSession = JSON.stringify({
      isAuthenticated: true,
      token: safeToken,
      sessionMode,
      sessionId: session?.sessionId || null,
      sessionUuid: session?.sessionUuid || null,
      currentUser: session?.currentUser,
      lastLoginAt: session?.lastLoginAt || Date.now()
    });

    try {
      window.sessionStorage.setItem(STORAGE_KEYS.authSession, serializedSession);
    } catch {
      // ignore storage write failure
    }

    // Mantem compatibilidade em sincroniza��o entre abas, mas sem token no fallback localStorage.
    // Importante: nao reescrever sessionStorage aqui para evitar apagar token da sessao online.
    try {
      const fallbackStorage = getFallbackSessionStorage();
      if (fallbackStorage) {
        fallbackStorage.setItem(STORAGE_KEYS.authSession, JSON.stringify({
          isAuthenticated: true,
          token: isOfflineSessionToken(safeToken) ? safeToken : "",
          sessionMode,
          sessionId: session?.sessionId || null,
          sessionUuid: session?.sessionUuid || null,
          currentUser: session?.currentUser,
          lastLoginAt: session?.lastLoginAt || Date.now()
        }));
      }
    } catch {
      // ignore fallback storage write failure
    }

    removeStoredSessionValue(STORAGE_KEYS.authToken);
  }

  if (session?.notice) {
    writeStoredSessionValue(STORAGE_KEYS.authNotice, session?.notice);
  } else {
    removeStoredSessionValue(STORAGE_KEYS.authNotice);
  }

  window.dispatchEvent(new CustomEvent(ERP_AUTH_EVENT, { detail: session }));
}

function isOfflineSessionToken(token) {
  return String(token || "").trim() === "offline-session";
}

function isBackendOrDatabaseUnavailable(error) {
  const rawMessage = String(error?.message || "").trim();
  const normalizedMessage = rawMessage.toLowerCase();
  const statusCode = Number(error?.response?.status || 0);

  if (statusCode >= 500) {
    return true;
  }

  return (
    normalizedMessage.includes("sqlstate")
    || normalizedMessage.includes("hy000")
    || normalizedMessage.includes("[2002]")
    || normalizedMessage.includes("ligacao a base de dados")
    || normalizedMessage.includes("liga��o � base de dados")
    || normalizedMessage.includes("conexao com base de dados")
    || normalizedMessage.includes("conex�o com base de dados")
    || normalizedMessage.includes("connection refused")
    || normalizedMessage.includes("actively refused")
    || normalizedMessage.includes("target machine actively refused")
    || normalizedMessage.includes("refusou ativamente")
    || normalizedMessage.includes("servidor indisponivel")
    || normalizedMessage.includes("servidor indispon�vel")
  );
}

function shouldRevokeOfflineAccessOnReconnect(error) {
  const message = String(error?.message || "").trim().toLowerCase();
  const statusCode = Number(error?.response?.status || 0);

  if (statusCode === 429) {
    return true;
  }

  const hasExplicitBlockSignal = (
    message.includes("bloqueado")
    || message.includes("blocked")
    || message.includes("assinatura")
    || message.includes("subscription")
    || message.includes("dispositivo")
    || message.includes("device")
    || message.includes("limite")
    || message.includes("acesso bloqueado")
  );

  if ((statusCode === 401 || statusCode === 403) && hasExplicitBlockSignal) {
    return true;
  }

  return false;
}

const PASSWORD_CHANGE_SYNC_TYPE = "changePassword";

export function useErpContext() {
  const [companies, setCompanies] = useState(COMPANIES);
  const [snapshot, setSnapshot] = useState(() => readSnapshot(COMPANIES));
  const [authSession, setAuthSession] = useState(readAuthSession);
  const passwordSyncInFlightRef = useRef(false);
  const logoutInFlightRef = useRef(false);
  const autoLogoutTriggeredRef = useRef(false);
  const refreshAuthInFlightRef = useRef(false);
  const offlineRecoveryInFlightRef = useRef(false);
  const deviceRegistrationInFlightRef = useRef(false);
  const orgDataInFlightRef = useRef(false);
  const globalPosOfflineSyncOwnerRef = useRef(`global-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`);
  const globalPosOfflineSyncRunnerRef = useRef(null);
  if (!globalPosOfflineSyncRunnerRef.current) {
    globalPosOfflineSyncRunnerRef.current = createPosOfflineSyncRunner(globalPosOfflineSyncOwnerRef.current);
  }
  const globalCustomerOfflineSyncOwnerRef = useRef(`global-customers-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`);
  const globalCustomerOfflineSyncRunnerRef = useRef(null);
  if (!globalCustomerOfflineSyncRunnerRef.current) {
    globalCustomerOfflineSyncRunnerRef.current = createCustomerOfflineSyncRunner(globalCustomerOfflineSyncOwnerRef.current);
  }
  const orgDataPromiseRef = useRef(null);
  const [permissionCatalog, setPermissionCatalog] = useState(() => readCachedPermissionCatalog());

  const redirectToLogin = (state = {}) => {
    if (typeof window === "undefined") {
      return;
    }

    const currentPath = String(window.location?.pathname || "");
    if (currentPath === "/login") {
      return;
    }

    const reason = String(state?.reason || "session-expired").trim() || "session-expired";
    // state?.notice !== undefined (em vez de só "state?.notice ||") — permite
    // a um chamador pedir explicitamente "sem aviso" (ex.: logout manual,
    // reason:"logout", notice:"") sem cair no aviso genérico de sessão
    // expirada, que seria enganador para uma ação deliberada do utilizador.
    // Só cai no fallback quando o chamador nem sequer passou "notice".
    const notice = state?.notice !== undefined
      ? String(state.notice).trim()
      : "Sessao expirada. Autentique-se novamente.";
    // "reason" mantém-se na URL (PublicLoginRoute em Routes.jsx depende
    // dele para saltar o ecrã /splash), mas "notice" deixou de ir — é
    // mostrado aqui, já.
    const target = `/login?reason=${encodeURIComponent(reason)}`;

    const goToLogin = () => {
      // Reconfirma no momento em que o reload de facto acontece — com a
      // pausa de 1.5s abaixo, é possível (ainda que raro) o utilizador já
      // ter chegado a /login por outra via entretanto.
      if (String(window.location?.pathname || "") === "/login") {
        return;
      }
      window.location.assign(target);
    };

    if (!notice) {
      goToLogin();
      return;
    }

    // Antes, o reload acontecia de imediato e o aviso só aparecia (via
    // reason/notice na URL) DEPOIS de aterrar em /login — com várias
    // chamadas 401 em sequência (heartbeat, polling do badge AGT,
    // auto-refresh do hub fiscal, etc.) isso dava a sensação de "vários
    // refresh" antes de perceber o que se tinha passado. O reload em si
    // mantém-se — ver comentário em Header.jsx sobre a corrida entre
    // instâncias de useErpContext ao navegar sem reload completo (bug já
    // confirmado nesta app) — só passa a acontecer depois de dar tempo
    // de ler o aviso, em vez de instantâneo, e o aviso mostra-se já na
    // página onde o utilizador está (ToastViewport fica montado na raiz
    // da app, sobrevive à espera).
    toast.warning(notice, { title: "Sessão", duration: 2200 });
    // Evita mostrar o mesmo aviso outra vez (authNotice) ao aterrar em
    // /login — acabou de ser mostrado aqui.
    removeStoredSessionValue(STORAGE_KEYS.authNotice);
    window.setTimeout(goToLogin, 1500);
  };

  useEffect(() => {
    let active = true;

    const hydrateFromOfflineStorage = async () => {
      await ensureOfflineStorageReady();
      if (!active) {
        return;
      }

      const offlineCompanies = readOfflineCompanies();
      const nextCompanies = offlineCompanies?.length ? offlineCompanies : [];
      const persistedOperationalContext = readOfflineOperationalContext();
      const persistedSnapshot = persistedOperationalContext?.snapshot && typeof persistedOperationalContext.snapshot === "object"
        ? persistedOperationalContext.snapshot
        : null;
      const persistedAuthSession = persistedOperationalContext?.authSession && typeof persistedOperationalContext.authSession === "object"
        ? persistedOperationalContext.authSession
        : null;

      let nextSnapshot = readSnapshot(nextCompanies);
      if ((!nextSnapshot?.companyId || !nextSnapshot?.storeId) && persistedSnapshot) {
        nextSnapshot = {
          ...nextSnapshot,
          ...persistedSnapshot,
        };
      }

      let nextAuthSession = readAuthSession();
      if ((!nextAuthSession?.isAuthenticated || !nextAuthSession?.currentUser) && persistedAuthSession?.isAuthenticated && persistedAuthSession?.currentUser) {
        const restoredToken = resolveSessionValidationToken(persistedAuthSession?.token);
        nextAuthSession = {
          isAuthenticated: true,
          currentUser: sanitizeUser(persistedAuthSession.currentUser, nextCompanies),
          token: restoredToken,
          sessionId: persistedAuthSession?.sessionId || null,
          sessionUuid: persistedAuthSession?.sessionUuid || null,
          lastLoginAt: persistedAuthSession?.lastLoginAt || null,
          notice: String(persistedAuthSession?.notice || "").trim(),
        };
        persistAuthSession(nextAuthSession);
      }

      setCompanies(nextCompanies);
      setSnapshot(nextSnapshot);
      setAuthSession(nextAuthSession);
      setPermissionCatalog(readCachedPermissionCatalog());
    };

    const handleOfflineStorageReady = () => {
      const offlineCompanies = readOfflineCompanies();
      const nextCompanies = offlineCompanies?.length ? offlineCompanies : [];
      setCompanies(nextCompanies);
      setSnapshot(readSnapshot(nextCompanies));
      setAuthSession(readAuthSession());
      setPermissionCatalog(readCachedPermissionCatalog());
    };

    void hydrateFromOfflineStorage();
    window.addEventListener(OFFLINE_STORAGE_READY_EVENT, handleOfflineStorageReady);

    return () => {
      active = false;
      window.removeEventListener(OFFLINE_STORAGE_READY_EVENT, handleOfflineStorageReady);
    };
  }, []);

  useEffect(() => {
    if (!authSession?.isAuthenticated || !authSession?.token || isOfflineSessionToken(authSession?.token)) {
      setPermissionCatalog(readCachedPermissionCatalog());
      return;
    }

    let active = true;

    (async () => {
      try {
        const payload = await apiListPermissions(authSession.token);
        if (!active) {
          return;
        }
        const items = Array.isArray(payload) ? payload : [];
        setPermissionCatalog(items.length > 0 ? items : readCachedPermissionCatalog());
      } catch {
        if (active) {
          setPermissionCatalog(readCachedPermissionCatalog());
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [authSession?.isAuthenticated, authSession?.token, authSession?.currentUser?.uuid]);

  const loadOrganizationData = async (token) => {
    const effectiveToken = String(token || "").trim();

    // Guard: se j� estiver em voo, retorna a promise em curso
    if (orgDataInFlightRef.current) {
      if (orgDataPromiseRef.current) {
        return orgDataPromiseRef.current;
      }
    }

    // Marca como em voo
    orgDataInFlightRef.current = true;

    const promise = (async () => {
      try {
        const bootstrap = await apiGetSessionBootstrap(effectiveToken);
        // Array.isArray (não só "?.length"): distingue um bootstrap que
        // respondeu com sucesso e disse definitivamente "0 empresas" (caso
        // normal de um utilizador novo, mesmo aqui de propósito) de um
        // bootstrap que veio sem a secção "companies.items" preenchida (aí
        // sim vale a pena um pedido extra a apiListEmpresas como
        // salvaguarda). Antes, QUALQUER lista vazia — incluindo a resposta
        // legítima "0 empresas" de um utilizador acabado de criar — caía
        // sempre no apiListEmpresas de seguida, duplicando o pedido de
        // rede sem nenhum ganho (ambos leem a mesma fonte de dados) e
        // atrasando desnecessariamente a chegada a /company-setup depois
        // do login.
        const bootstrapItemsIsArray = Array.isArray(bootstrap?.companies?.items);
        const bootstrapCompanies = (bootstrap?.companies?.items || [])
          .map((company) => {
            const normalizedCompany = normalizeCompanyRecord(company);
            const stores = (company?.stores || []).map((store) => normalizeStoreRecord(store, normalizedCompany?.id));
            return {
              ...normalizedCompany,
              stores: buildAggregateStore(stores, normalizedCompany?.id, normalizedCompany?.uuid),
            };
          });

        if (bootstrapCompanies?.length || bootstrapItemsIsArray) {
          setCompanies(bootstrapCompanies);
          writeOfflineCompanies(bootstrapCompanies);
          return bootstrapCompanies;
        }

        const companiesResponse = await apiListEmpresas(effectiveToken);
        const baseCompanies = (companiesResponse?.items || [])
          .map((company) => normalizeCompanyRecord(company));

        if (!baseCompanies?.length) {
          setCompanies([]);
          writeOfflineCompanies([]);
          return [];
        }

      let hydratedCompanies = baseCompanies;
      // Promise.allSettled em vez de Promise.all: um único pedido de lojas
      // lento/a falhar (comum em dispositivos ou ligações lentas) não deve
      // derrubar a hidratação de TODAS as empresas — mantém as lojas já
      // conhecidas dessa empresa (memória actual ou cache offline) em vez de
      // a deixar sem lojas nenhumas, o que fazia `hasEffectiveActiveSubscription`
      // avaliar como inactiva e prender o utilizador em /subscription-plans.
      const storesSettled = await Promise.allSettled(baseCompanies.map(async (company) => {
        const storesResponse = await apiListLojas(effectiveToken, {
          companyUuid: company?.uuid,
          companyCode: company?.id,
        });

        return {
          companyId: company?.id,
          stores: (storesResponse?.items || []).map((store) => normalizeStoreRecord(store, company?.id)),
        };
      }));

      storesSettled.forEach((result, index) => {
        const company = baseCompanies[index];
        if (result.status === "fulfilled") {
          hydratedCompanies = mergeCompanyStores(hydratedCompanies, result.value.companyId, result.value.stores);
          return;
        }

        const previousStores = companies.find((item) => item?.id === company?.id)?.stores
          || readOfflineCompanies()?.find((item) => item?.id === company?.id)?.stores
          || [];
        hydratedCompanies = mergeCompanyStores(hydratedCompanies, company?.id, previousStores);
      });

      setCompanies(hydratedCompanies);
      writeOfflineCompanies(hydratedCompanies);
      return hydratedCompanies;
    } catch (error) {
      // Antes de confiar numa cache offline (potencialmente desactualizada,
      // ex.: sem assinatura activa registada), tenta mais uma vez em caso de
      // falha transitória — dispositivos/ligações lentas por vezes só
      // precisam de mais tempo, não estão realmente offline.
      if (isNetworkUnavailable(error) || isBackendOrDatabaseUnavailable(error)) {
        try {
          await new Promise((resolve) => window.setTimeout(resolve, 2500));
          const retryBootstrap = await apiGetSessionBootstrap(effectiveToken);
          const retryCompanies = (retryBootstrap?.companies?.items || [])
            .map((company) => {
              const normalizedCompany = normalizeCompanyRecord(company);
              const stores = (company?.stores || []).map((store) => normalizeStoreRecord(store, normalizedCompany?.id));
              return {
                ...normalizedCompany,
                stores: buildAggregateStore(stores, normalizedCompany?.id, normalizedCompany?.uuid),
              };
            });

          if (retryCompanies?.length) {
            setCompanies(retryCompanies);
            writeOfflineCompanies(retryCompanies);
            return retryCompanies;
          }
        } catch {
          // Segunda tentativa também falhou — segue para a cache offline abaixo.
        }
      }

      const offlineCompanies = readOfflineCompanies();
      const fallbackCompanies = offlineCompanies?.length ? offlineCompanies : [];
      setCompanies(fallbackCompanies);
      return fallbackCompanies;
    } finally {
      // Limpar state ap�s conclus�o
      orgDataInFlightRef.current = false;
      orgDataPromiseRef.current = null;
    }
    })();

    // Cache a promise para futuras chamadas paralelas
    orgDataPromiseRef.current = promise;
    return promise;
  };

  useEffect(() => {
    const syncSnapshot = (nextSnapshot) => {
      // Usa a vers�o mais recente de companies para evitar normaliza��o com estado obsoleto.
      const company = getCompanyById(nextSnapshot?.companyId, companies);
      const store = getStoreById(company, nextSnapshot?.storeId);
      const fiscal = getFiscalByValue(nextSnapshot?.fiscalValue);
      const role = getRoleByValue(nextSnapshot?.roleValue);
      const themeValue = getThemeByValue(nextSnapshot?.themeValue)?.value;

      setSnapshot((prevSnapshot) => {
        const normalizedSnapshot = {
          companyId: company?.id ?? prevSnapshot?.companyId,
          storeId: store?.id ?? prevSnapshot?.storeId,
          fiscalValue: fiscal?.value ?? prevSnapshot?.fiscalValue,
          roleValue: role?.value ?? prevSnapshot?.roleValue,
          themeValue: themeValue ?? prevSnapshot?.themeValue,
        };

        const isUnchanged =
          prevSnapshot?.companyId === normalizedSnapshot.companyId
          && prevSnapshot?.storeId === normalizedSnapshot.storeId
          && prevSnapshot?.fiscalValue === normalizedSnapshot.fiscalValue
          && prevSnapshot?.roleValue === normalizedSnapshot.roleValue
          && prevSnapshot?.themeValue === normalizedSnapshot.themeValue;

        return isUnchanged ? prevSnapshot : normalizedSnapshot;
      });
    };

    const handleEvent = (event) => syncSnapshot(event?.detail || readSnapshot(companies));
    const handleStorage = () => syncSnapshot(readSnapshot(companies));

    window.addEventListener(ERP_CONTEXT_EVENT, handleEvent);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(ERP_CONTEXT_EVENT, handleEvent);
      window.removeEventListener("storage", handleStorage);
    };
  }, [companies]);

  useEffect(() => {
    const syncAuth = (nextAuth) => {
      const resolvedAuth = nextAuth?.currentUser ? {
        ...nextAuth,
        currentUser: sanitizeUser(nextAuth?.currentUser, companies)
      } : readAuthSession();

      setAuthSession({
        isAuthenticated: Boolean(resolvedAuth?.isAuthenticated && resolvedAuth?.currentUser),
        currentUser: resolvedAuth?.currentUser || null,
        token: resolvedAuth?.token || "",
        sessionId: resolvedAuth?.sessionId || null,
        sessionUuid: resolvedAuth?.sessionUuid || null,
        lastLoginAt: resolvedAuth?.lastLoginAt || null,
        notice: resolvedAuth?.notice || ""
      });
    };

    const handleAuthEvent = (event) => syncAuth(event?.detail || readAuthSession());
    const handleStorage = () => syncAuth(readAuthSession());

    window.addEventListener(ERP_AUTH_EVENT, handleAuthEvent);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(ERP_AUTH_EVENT, handleAuthEvent);
      window.removeEventListener("storage", handleStorage);
    };
  }, [companies]);

  useEffect(() => {
    let active = true;

    const persistOperationalContext = async () => {
      if (!authSession?.isAuthenticated || !authSession?.currentUser) {
        await clearOfflineOperationalContext();
        return;
      }

      await persistOfflineOperationalContext({
        authSession: {
          isAuthenticated: true,
          token: String(authSession?.token || "").trim(),
          sessionId: authSession?.sessionId || null,
          sessionUuid: authSession?.sessionUuid || null,
          lastLoginAt: authSession?.lastLoginAt || Date.now(),
          notice: String(authSession?.notice || "").trim(),
          currentUser: authSession?.currentUser || null,
        },
        snapshot: snapshot || null,
        scope: {
          companyUuid: String(authSession?.currentUser?.companyUuid || "").trim() || null,
          storeUuid: String(authSession?.currentUser?.storeUuid || "").trim() || null,
          userUuid: String(authSession?.currentUser?.uuid || "").trim() || null,
        },
      });
    };

    void (async () => {
      try {
        await persistOperationalContext();
      } catch {
        if (!active) {
          return;
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [authSession, snapshot]);

  useEffect(() => {
    if (!authSession?.isAuthenticated || isOfflineSessionToken(authSession?.token)) return;

    const effectiveToken = resolveSessionValidationToken(authSession?.token);

    let active = true;

    (async () => {
      try {
        const sessionUser = await apiValidateSession(effectiveToken);
        const nextCompanies = await loadOrganizationData(effectiveToken);

        if (!active) return;

        const sanitizedUser = sanitizeUser(sessionUser, nextCompanies);
        const nextAuthSession = {
          ...authSession,
          isAuthenticated: true,
          currentUser: sanitizedUser,
          sessionId: sessionUser?.session_id || authSession?.sessionId || null,
          sessionUuid: sessionUser?.session_uuid || authSession?.sessionUuid || null,
          lastLoginAt: authSession?.lastLoginAt || Date.now(),
          notice: ""
        };

        setAuthSession(nextAuthSession);
        persistAuthSession(nextAuthSession);
        updateSnapshot({
          companyId: sanitizedUser?.companyId,
          storeId: sanitizedUser?.storeId,
          roleValue: sanitizedUser?.roleValue,
        }, nextCompanies);
      } catch (error) {
        if (!active) return;

        if ((isNetworkUnavailable(error) || isBackendOrDatabaseUnavailable(error)) && authSession?.currentUser) {
          const offlineCompanies = readOfflineCompanies();
          if (offlineCompanies?.length) {
            setCompanies(offlineCompanies);
          }

          const nextAuthSession = {
            ...authSession,
            isAuthenticated: true,
            currentUser: authSession?.currentUser,
            token: isOfflineSessionToken(authSession?.token)
              ? authSession?.token
              : "offline-session",
            notice: "Modo offline activo. As alteracoes ficam pendentes ate a proxima sincronizacao.",
          };

          setAuthSession(nextAuthSession);
          persistAuthSession(nextAuthSession);
          return;
        }

        const nextAuthSession = {
          isAuthenticated: false,
          currentUser: null,
          token: "",
          sessionId: null,
          sessionUuid: null,
          lastLoginAt: null,
          notice: error?.message || "Sessao expirada. Autentique-se novamente."
        };

        setAuthSession(nextAuthSession);
        persistAuthSession(nextAuthSession);
        redirectToLogin({
          reason: "session-invalid",
          notice: nextAuthSession.notice,
        });
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  const activeCompany = useMemo(() => getCompanyById(snapshot?.companyId, companies), [companies, snapshot?.companyId]);
  const activeFiscal = useMemo(() => getFiscalByValue(snapshot?.fiscalValue), [snapshot?.fiscalValue]);
  const effectiveRoleValue = authSession?.isAuthenticated
    ? (authSession?.currentUser?.roleValue || snapshot?.roleValue)
    : snapshot?.roleValue;
  const activeRole = useMemo(() => getRoleByValue(effectiveRoleValue), [effectiveRoleValue]);
  const activeTheme = useMemo(() => getThemeByValue(snapshot?.themeValue), [snapshot?.themeValue]);
  const isManagerScope = isManagerRole(activeRole?.value);
  const hasCrossStoreScope = canUseCrossStoreScope(authSession?.currentUser, activeRole?.value);
  const isStoreLockedRole = !hasCrossStoreScope;
  const availableStores = useMemo(() => {
    const stores = activeCompany?.stores || [];
    if (hasCrossStoreScope) {
      return stores;
    }

    const userStoreId = String(authSession?.currentUser?.storeId || "").trim();
    const userStoreUuid = String(authSession?.currentUser?.storeUuid || "").trim();
    const nonAggregateStores = stores.filter((store) => String(store?.id || "").trim() !== "all");
    const scopedStore = nonAggregateStores.find((store) => (
      (userStoreId && String(store?.id || "").trim() === userStoreId)
      || (userStoreUuid && String(store?.uuid || "").trim() === userStoreUuid)
    )) || nonAggregateStores.find((store) => store?.principal) || nonAggregateStores[0] || null;

    return scopedStore ? [scopedStore] : [];
  }, [activeCompany, authSession?.currentUser?.storeId, authSession?.currentUser?.storeUuid, hasCrossStoreScope]);
  const activeStore = useMemo(() => {
    if (hasCrossStoreScope) {
      return getStoreById(activeCompany, snapshot?.storeId);
    }

    const userStoreId = String(authSession?.currentUser?.storeId || "").trim();
    const userStoreUuid = String(authSession?.currentUser?.storeUuid || "").trim();
    const stores = availableStores || [];
    const snapshotStoreId = String(snapshot?.storeId || "").trim();

    return stores.find((store) => String(store?.id || "").trim() === snapshotStoreId)
      || stores.find((store) => String(store?.id || "").trim() === userStoreId)
      || stores.find((store) => userStoreUuid && String(store?.uuid || "").trim() === userStoreUuid)
      || stores[0]
      || null;
  }, [activeCompany, authSession?.currentUser?.storeId, authSession?.currentUser?.storeUuid, availableStores, hasCrossStoreScope, snapshot?.storeId]);
  const isConsolidatedScope = Boolean(hasCrossStoreScope && activeStore?.id === "all");
  const accessibleModules = useMemo(() => {
    return buildAccessibleModuleSet({
      roleValue: activeRole?.value,
      isConsolidated: isConsolidatedScope,
      permissionCodes: authSession?.currentUser?.permissionCodes || [],
      permissionCatalog,
    });
  }, [activeRole?.value, authSession?.currentUser?.permissionCodes, isConsolidatedScope, permissionCatalog]);
  const scopeUuids = useMemo(() => ({
    userUuid: authSession?.currentUser?.uuid || null,
    companyUuid: authSession?.currentUser?.companyUuid || activeCompany?.uuid || null,
    storeUuid: activeStore?.uuid || authSession?.currentUser?.storeUuid || null,
  }), [activeCompany?.uuid, activeStore?.uuid, authSession?.currentUser?.companyUuid, authSession?.currentUser?.storeUuid, authSession?.currentUser?.uuid]);

  useEffect(() => {
    if (!authSession?.isAuthenticated || hasCrossStoreScope) {
      return;
    }

    const snapshotStoreId = String(snapshot?.storeId || "").trim();
    const activeScopedStoreId = String(activeStore?.id || "").trim();
    if (!activeScopedStoreId) {
      return;
    }

    if (snapshotStoreId === activeScopedStoreId && snapshotStoreId !== "all") {
      return;
    }

    updateSnapshot({ storeId: activeScopedStoreId }, companies);
  }, [activeStore?.id, authSession?.isAuthenticated, companies, hasCrossStoreScope, snapshot?.storeId]);

  useEffect(() => {
    const isDesktopRuntime = typeof window !== "undefined"
      && (window.location.hostname === "tauri.localhost" || typeof window.__TAURI_INTERNALS__ !== "undefined");

    if (!isDesktopRuntime) {
      return;
    }

    if (!authSession?.isAuthenticated || !authSession?.token || !activeCompany?.uuid) {
      return;
    }

    let active = true;

    (async () => {
      try {
        await apiSyncAgtPrivateKeyToLocalVault(authSession.token, {
          companyUuid: activeCompany.uuid,
          storeUuid: activeStore?.uuid || null,
        });
      } catch {
        if (!active) {
          return;
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [activeCompany?.uuid, activeStore?.uuid, authSession?.isAuthenticated, authSession?.token]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (!authSession?.isAuthenticated || !authSession?.token || isOfflineSessionToken(authSession?.token)) {
      return;
    }

    const companyUuid = String(authSession?.currentUser?.companyUuid || "").trim();
    const userUuid = String(authSession?.currentUser?.uuid || "").trim();
    if (!companyUuid || !userUuid) {
      return;
    }

    const registration = buildSubscriptionDevicePayload(authSession?.currentUser);
    if (!registration?.payload?.fingerprint || !registration?.deviceId) {
      return;
    }

    const regKey = `${SUBSCRIPTION_DEVICE_REG_PREFIX}.${companyUuid}.${userUuid}.${registration.deviceId}`;
    const lastSync = Number(readLocalStorageValue(regKey) || 0);
    const now = Date.now();

    if (Number.isFinite(lastSync) && lastSync > 0 && now - lastSync < SUBSCRIPTION_DEVICE_REG_INTERVAL_MS) {
      return;
    }

    if (deviceRegistrationInFlightRef.current) {
      return;
    }

    let cancelled = false;
    deviceRegistrationInFlightRef.current = true;

    (async () => {
      try {
        await apiRegisterSubscriptionDevice(authSession.token, registration.payload);
        if (!cancelled) {
          writeLocalStorageValue(regKey, String(Date.now()));
        }
      } catch (error) {
        const statusCode = Number(error?.response?.status || 0);
        const isDeviceLimitError = statusCode === 429;

        if (isDeviceLimitError && !cancelled) {
          const limit = Number(error?.response?.data?.limit || 0);
          const safeLimit = Number.isFinite(limit) && limit > 0 ? limit : 1;
          const notice = `Limite de dispositivos excedido. Esta empresa permite no maximo ${safeLimit} dispositivos ativos.`;

          const nextAuthSession = {
            isAuthenticated: false,
            currentUser: null,
            token: "",
            sessionId: null,
            sessionUuid: null,
            lastLoginAt: null,
            notice,
          };

          setAuthSession(nextAuthSession);
          persistAuthSession(nextAuthSession);
          redirectToLogin({
            reason: "device-limit",
            notice,
          });
          return;
        }

        // Erros transitórios de registo não devem bloquear o fluxo de autenticação
        // — mas também não podem ser tentados de novo a cada re-render enquanto a
        // causa persistir (ex.: assinatura expirada = 403 permanente até renovar).
        // Sem isto, qualquer mudança de referência em authSession.currentUser
        // disparava este POST outra vez de imediato, martelando o endpoint em
        // loop (visível na consola como vários 403 seguidos). Aplica o mesmo
        // cooldown que um sucesso teria.
        if (!cancelled) {
          writeLocalStorageValue(regKey, String(Date.now()));
        }
      } finally {
        deviceRegistrationInFlightRef.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authSession?.currentUser, authSession?.isAuthenticated, authSession?.token]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    if (!authSession?.isAuthenticated || !isOfflineSessionToken(authSession?.token) || !authSession?.currentUser) {
      return undefined;
    }

    let active = true;

    const attemptRecoverOnlineSession = async () => {
      if (!active || offlineRecoveryInFlightRef.current || refreshAuthInFlightRef.current) {
        return;
      }

      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        return;
      }

      offlineRecoveryInFlightRef.current = true;

      try {
        const offlineRecord = await getOfflineLoginRecordForUser(authSession.currentUser);
        const recoveryToken = String(offlineRecord?.session?.token || "").trim();
        if (!recoveryToken) {
          return;
        }

        const sessionUser = await apiValidateSession(recoveryToken);
        const nextCompanies = await loadOrganizationData(recoveryToken);

        if (!active) {
          return;
        }

        const sanitizedUser = sanitizeUser(sessionUser, nextCompanies);
        const nextAuthSession = {
          ...authSession,
          isAuthenticated: true,
          currentUser: sanitizedUser,
          token: recoveryToken,
          sessionId: sessionUser?.session_id || offlineRecord?.session?.sessionId || null,
          sessionUuid: sessionUser?.session_uuid || offlineRecord?.session?.sessionUuid || null,
          lastLoginAt: authSession?.lastLoginAt || Date.now(),
          notice: "Sessao online restaurada.",
        };

        setAuthSession(nextAuthSession);
        persistAuthSession(nextAuthSession);
        updateSnapshot({
          companyId: sanitizedUser?.companyId,
          storeId: sanitizedUser?.storeId,
          roleValue: sanitizedUser?.roleValue,
        }, nextCompanies);
      } catch (error) {
        if (!active) {
          return;
        }

        if (isNetworkUnavailable(error) || isBackendOrDatabaseUnavailable(error)) {
          return;
        }

        if (!shouldRevokeOfflineAccessOnReconnect(error)) {
          return;
        }

        await invalidateOfflineLoginCacheForUser(authSession.currentUser);
        await logout("offline-access-revoked", {
          skipRemote: true,
          notice: error?.message || "O acesso offline foi revogado. Autentique-se novamente.",
        });

        redirectToLogin({
          reason: "offline-access-revoked",
          notice: error?.message || "O acesso offline foi revogado. Autentique-se novamente.",
        });
      } finally {
        offlineRecoveryInFlightRef.current = false;
      }
    };

    const handleOnline = () => {
      void attemptRecoverOnlineSession();
    };

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        void attemptRecoverOnlineSession();
      }
    };

    window.addEventListener("online", handleOnline);
    document.addEventListener("visibilitychange", handleVisibility);
    void attemptRecoverOnlineSession();

    return () => {
      active = false;
      window.removeEventListener("online", handleOnline);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [authSession, logout]);

  useEffect(() => {
    const isDesktopRuntime = typeof window !== "undefined"
      && (window.location.hostname === "tauri.localhost" || typeof window.__TAURI_INTERNALS__ !== "undefined");

    if (!isDesktopRuntime) {
      return;
    }

    if (!authSession?.isAuthenticated || !authSession?.token || !authSession?.currentUser?.uuid || !activeCompany?.uuid) {
      return;
    }

    let cancelled = false;

    const runHeartbeat = async () => {
      const lastHeartbeatAt = Number(readLocalStorageValue(APP_CLIENT_VERSION_HEARTBEAT_KEY) || 0);
      const now = Date.now();
      if (Number.isFinite(lastHeartbeatAt) && lastHeartbeatAt > 0 && now - lastHeartbeatAt < APP_CLIENT_VERSION_HEARTBEAT_INTERVAL_MS) {
        return;
      }

      try {
        const [{ getVersion }, updateCheckModule] = await Promise.all([
          import("@tauri-apps/api/app"),
          import("./useAppUpdateCheck"),
        ]);

        const appVersion = await getVersion();
        let cachedUpdate = typeof updateCheckModule?.readCachedResult === "function"
          ? updateCheckModule.readCachedResult()
          : null;

        if (!cachedUpdate?.version) {
          try {
            const response = await fetch(APP_VERSION_URL, {
              method: "GET",
              cache: "no-store",
            });
            const payload = await response.json();
            if (payload?.ok && payload?.version) {
              cachedUpdate = payload;
            }
          } catch {
            // ignore and continue with available metadata
          }
        }

        await apiRegisterAppClientVersion(authSession.token, {
          app_version: appVersion,
          min_supported_version: String(cachedUpdate?.min_supported_version || "").trim(),
          recommended_version: String(cachedUpdate?.version || "").trim(),
          device_id: ensureDesktopDeviceId(),
          device_name: String(window.navigator?.platform || "Desktop").trim() || "Desktop",
          platform: "desktop",
          runtime: "tauri",
          hostname: String(window.location?.hostname || "").trim(),
        });

        if (!cancelled) {
          writeLocalStorageValue(APP_CLIENT_VERSION_HEARTBEAT_KEY, String(now));
        }
      } catch {
        if (cancelled) {
          return;
        }
      }
    };

    void runHeartbeat();

    return () => {
      cancelled = true;
    };
  }, [activeCompany?.uuid, authSession?.currentUser?.uuid, authSession?.isAuthenticated, authSession?.token]);

  useEffect(() => {
    if (typeof document === "undefined") return;

    const root = document.documentElement;
    root.dataset.theme = activeTheme?.value || ERP_THEMES?.[0]?.value;
    root.classList.toggle("dark", activeTheme?.value === "midnight");
  }, [activeTheme?.value]);

  const updateSnapshot = (partial, sourceCompanies = companies) => {
    setSnapshot((prevSnapshot) => {
      const nextCompany = getCompanyById(partial?.companyId ?? prevSnapshot?.companyId, sourceCompanies);
      const nextStore = getStoreById(nextCompany, partial?.storeId ?? prevSnapshot?.storeId);
      const nextFiscal = getFiscalByValue(partial?.fiscalValue ?? prevSnapshot?.fiscalValue);
      const nextRole = getRoleByValue(partial?.roleValue ?? prevSnapshot?.roleValue);
      const nextTheme = getThemeByValue(partial?.themeValue ?? prevSnapshot?.themeValue);

      const nextSnapshot = {
        companyId: nextCompany?.id ?? prevSnapshot?.companyId,
        storeId: nextStore?.id ?? prevSnapshot?.storeId,
        fiscalValue: nextFiscal?.value ?? prevSnapshot?.fiscalValue,
        roleValue: nextRole?.value ?? prevSnapshot?.roleValue,
        themeValue: nextTheme?.value ?? prevSnapshot?.themeValue
      };

      const isUnchanged =
        prevSnapshot?.companyId === nextSnapshot.companyId
        && prevSnapshot?.storeId === nextSnapshot.storeId
        && prevSnapshot?.fiscalValue === nextSnapshot.fiscalValue
        && prevSnapshot?.roleValue === nextSnapshot.roleValue
        && prevSnapshot?.themeValue === nextSnapshot.themeValue;

      if (isUnchanged) {
        return prevSnapshot;
      }

      // Persistencia em microtask evita setState em cadeia durante a fase de render.
      if (typeof queueMicrotask === "function") {
        queueMicrotask(() => persistSnapshot(nextSnapshot));
      } else {
        setTimeout(() => persistSnapshot(nextSnapshot), 0);
      }

      return nextSnapshot;
    });
  };

  const acknowledgeLocalPasswordChange = () => {
    if (!authSession?.currentUser) {
      return;
    }

    const nextAuthSession = {
      ...authSession,
      currentUser: {
        ...authSession.currentUser,
        passwordChangeRequired: false,
        passwordChangeReason: null,
      },
    };

    setAuthSession(nextAuthSession);
    persistAuthSession(nextAuthSession);
  };

  const setAuthenticatedUser = (user, authMeta = {}, notice = "", sourceCompanies = companies) => {
    const safeUser = sanitizeUser(user, sourceCompanies);

    // Evita reaproveitar snapshot antigo de p�ginas bloqueadas ao trocar de sess�o/dispositivo.
    clearCachedBlockedPagesSnapshot();

    const nextAuthSession = {
      isAuthenticated: Boolean(safeUser),
      currentUser: safeUser,
      token: authMeta?.token || "",
      sessionId: authMeta?.sessionId || null,
      sessionUuid: authMeta?.sessionUuid || null,
      lastLoginAt: Date.now(),
      notice
    };

    setAuthSession(nextAuthSession);
    persistAuthSession(nextAuthSession);

    if (safeUser) {
      window.localStorage.setItem(STORAGE_KEYS.splashForceShow, "1");
      updateSnapshot({
        companyId: safeUser?.companyId,
        roleValue: safeUser?.roleValue,
        storeId: safeUser?.storeId || getCompanyById(safeUser?.companyId, sourceCompanies)?.stores?.[0]?.id
      }, sourceCompanies);
    }
  };

  const login = async ({ email, phone, password }) => {
    const credential = String(email || phone || "").trim();

    const shouldFallbackToOfflineLogin = (error) => {
      if (isNetworkUnavailable(error)) {
        return true;
      }

      const statusCode = Number(error?.response?.status || 0);
      // apiLogin (authApi.js) envolve o erro em buildApiError, que reescreve
      // `.message` para um texto amigável mas preserva o erro tecnico
      // original em `.cause` — sem o incluir aqui, uma falha de ligação real
      // deixa de bater com os padrões abaixo e o fallback offline nunca é
      // acionado.
      const normalizedMessage = `${error?.message || ""} ${error?.cause?.message || ""}`.toLowerCase();

      const isBackendOrDatabaseUnavailable = (
        statusCode >= 500
        || normalizedMessage.includes("sqlstate")
        || normalizedMessage.includes("hy000")
        || normalizedMessage.includes("[2002]")
        || normalizedMessage.includes("ligacao a base de dados")
        || normalizedMessage.includes("liga��o � base de dados")
        || normalizedMessage.includes("connection refused")
        || normalizedMessage.includes("actively refused")
        || normalizedMessage.includes("refusou ativamente")
        || normalizedMessage.includes("servidor indisponivel")
        || normalizedMessage.includes("servidor indispon�vel")
      );

      return isBackendOrDatabaseUnavailable;
    };

    const resolveFriendlyLoginMessage = (error) => {
      const rawMessage = String(error?.message || "").trim();
      const normalizedMessage = `${rawMessage} ${error?.cause?.message || ""}`.toLowerCase();
      const statusCode = Number(error?.response?.status || 0);

      const isDatabaseConnectionFailure = (
        normalizedMessage.includes("sqlstate")
        || normalizedMessage.includes("hy000")
        || normalizedMessage.includes("[2002]")
        || normalizedMessage.includes("ligacao a base de dados")
        || normalizedMessage.includes("liga��o � base de dados")
        || normalizedMessage.includes("conexao com base de dados")
        || normalizedMessage.includes("connection refused")
        || normalizedMessage.includes("actively refused")
        || normalizedMessage.includes("target machine actively refused")
        || normalizedMessage.includes("refusou ativamente")
      );

      if (isDatabaseConnectionFailure || statusCode >= 500) {
        return "Nao foi possivel autenticar agora. O servidor esta temporariamente indisponivel. Tente novamente em instantes.";
      }

      // Restrição de acesso por incumprimento de pagamento (Admin > Planos)
      // também chega como 401 (ver AuthService::establishSession()) — sem
      // este caso à parte, cai na regra genérica abaixo e o utilizador via
      // sempre "Credenciais inválidas", escondendo o motivo real e
      // confundindo quem tenta entrar com a senha certa.
      if (error?.apiCode === "PAYMENT_RESTRICTED") {
        return rawMessage || "Acesso restrito por pendência de pagamento.";
      }

      if (statusCode === 401 || statusCode === 403) {
        return "Credenciais inv�lidas. Verifique o e-mail/telefone e a senha.";
      }

      return rawMessage || "Credenciais inv�lidas. Verifique o e-mail/telefone e a senha.";
    };

    try {
      const result = await apiLogin({ email, phone, password });
      const sessionToken = resolveSessionTokenFromLoginResult(result);
      if (!sessionToken) {
        return {
          ok: false,
          message: "Falha ao iniciar sess�o: token de sess�o n�o foi devolvido pelo servidor. Tente novamente.",
        };
      }

      const nextCompanies = await loadOrganizationData(sessionToken);
      const user = sanitizeUser(result?.user, nextCompanies);

      const scopeCheck = validateUserScopeAtLogin(user);
      if (!scopeCheck.ok) {
        try {
          await apiLogout(sessionToken || "");
        } catch {
          // ignora falha remota de logout em caso de sess�o j� inv�lida
        }

        return {
          ok: false,
          message: scopeCheck.message,
        };
      }

      setAuthenticatedUser(user, {
        token: sessionToken,
        sessionId: result?.session?.id,
        sessionUuid: result?.session?.uuid,
      }, "", nextCompanies);

      await cacheOfflineLoginRecord({
        email,
        phone,
        password,
        user,
        companies: nextCompanies,
        session: {
          token: sessionToken,
          sessionId: result?.session?.id,
          sessionUuid: result?.session?.uuid,
        },
      });

      return {
        ok: true,
        user,
        companies: nextCompanies,
      };
    } catch (error) {
      if (shouldFallbackToOfflineLogin(error)) {
        const offlineResult = await tryOfflineLogin({ email, phone, password });
        if (offlineResult?.ok) {
          const offlineCompanies = offlineResult?.companies?.length ? offlineResult.companies : readOfflineCompanies();
          const nextCompanies = offlineCompanies?.length ? offlineCompanies : [];
          setCompanies(nextCompanies);
          const user = sanitizeUser(offlineResult?.user, nextCompanies);

          const scopeCheck = validateUserScopeAtLogin(user);
          if (!scopeCheck.ok) {
            return {
              ok: false,
              message: scopeCheck.message,
            };
          }

          setAuthenticatedUser(user, {
            token: offlineResult?.token,
            sessionId: offlineResult?.sessionId,
            sessionUuid: offlineResult?.sessionUuid,
          }, "Modo offline activo. As alteracoes ficam pendentes ate a proxima sincronizacao.", nextCompanies);

          return {
            ok: true,
            user,
            companies: nextCompanies,
            offline: true,
          };
        }

        return {
          ok: false,
          message: offlineResult?.message || "Sem ligacao ao servidor e sem cache offline valido para este utilizador.",
        };
      }

      return {
        ok: false,
        message: resolveFriendlyLoginMessage(error),
        code: error?.apiCode || null,
        // Só preenchido quando code === "PAYMENT_RESTRICTED" — ver
        // PaymentRestrictedException.php / login.php (o payload extra que o
        // backend devolve especificamente para este caso).
        restrictionReason: error?.apiData?.reason || null,
        restrictionCommitmentDate: error?.apiData?.commitment_date || null,
      };
    }
  };

  // Autentica com PIN de 6 dígitos, válido apenas no dispositivo onde foi
  // configurado (ensureSubscriptionDeviceId já identifica esse dispositivo
  // de forma estável). Não tem fallback offline — PIN sempre exige o
  // servidor, ao contrário do login por password que tem cache local.
  const loginWithPin = async ({ identifier, pin }) => {
    const deviceId = ensureSubscriptionDeviceId();

    try {
      const result = await apiLoginWithPin({ identifier, deviceId, pin });
      const sessionToken = resolveSessionTokenFromLoginResult(result);
      if (!sessionToken) {
        return {
          ok: false,
          message: "Falha ao iniciar sessão: token de sessão não foi devolvido pelo servidor. Tente novamente.",
        };
      }

      const nextCompanies = await loadOrganizationData(sessionToken);
      const user = sanitizeUser(result?.user, nextCompanies);

      const scopeCheck = validateUserScopeAtLogin(user);
      if (!scopeCheck.ok) {
        try {
          await apiLogout(sessionToken || "");
        } catch {
          // ignora falha remota de logout em caso de sessão já inválida
        }

        return { ok: false, message: scopeCheck.message };
      }

      setAuthenticatedUser(user, {
        token: sessionToken,
        sessionId: result?.session?.id,
        sessionUuid: result?.session?.uuid,
      }, "", nextCompanies);

      return { ok: true, user, companies: nextCompanies };
    } catch (error) {
      return {
        ok: false,
        message: String(error?.message || "Não foi possível autenticar com o PIN."),
        locked: Boolean(error?.apiPayload?.locked),
      };
    }
  };

  const setLoginPin = async ({ pin, deviceName } = {}) => {
    const token = authSession?.token;
    if (!token) {
      return { ok: false, message: "Sessão inválida para configurar o PIN." };
    }

    const deviceId = ensureSubscriptionDeviceId();

    try {
      await apiSetLoginPin(token, { pin, deviceId, deviceName });
      // Junta-se à lista de contas com PIN neste dispositivo em vez de
      // substituir — outras contas já configuradas aqui continuam intactas.
      // Usa sempre o email como identificador: é a única credencial garantida
      // (NOT NULL na BD) — currentUser (sanitizeUser) não expõe telefone.
      upsertLoginPinProfile({
        identifier: authSession?.currentUser?.email,
        userName: authSession?.currentUser?.name,
      });

      return { ok: true };
    } catch (error) {
      return { ok: false, message: String(error?.message || "Não foi possível configurar o PIN.") };
    }
  };

  const removeLoginPin = async () => {
    const token = authSession?.token;
    const deviceId = ensureSubscriptionDeviceId();

    try {
      if (token) {
        await apiRemoveLoginPin(token, { deviceId });
      }
    } catch {
      // mesmo que a remoção remota falhe, limpa a marca local
    } finally {
      // Remove só a conta actual da lista — não mexe no PIN de outras contas
      // configuradas neste mesmo dispositivo.
      removeLoginPinProfile(authSession?.currentUser?.email);
    }

    return { ok: true };
  };

  async function logout(reason = "manual", options = {}) {
    if (logoutInFlightRef.current) {
      return { ok: true, remoteSynced: false, inFlight: true };
    }

    logoutInFlightRef.current = true;
    const sessionToken = options?.token || authSession?.token;

    try {
      clearCachedBlockedPagesSnapshot();
      clearStoredScopeSessionValues({ keepTheme: true });

      const nextAuthSession = {
        isAuthenticated: false,
        currentUser: null,
        token: "",
        sessionId: null,
        sessionUuid: null,
        lastLoginAt: null,
        notice: options?.notice || (reason === "inactive" ? "Sess�o bloqueada por inatividade. Autentique-se novamente." : "")
      };

      setAuthSession(nextAuthSession);
      persistAuthSession(nextAuthSession);
      await clearOfflineOperationalContext();

      if (!options?.skipRemote && sessionToken && !isOfflineSessionToken(sessionToken)) {
        const remoteSynced = await apiLogout(sessionToken);
        return { ok: true, remoteSynced: Boolean(remoteSynced) };
      }

      return {
        ok: true,
        remoteSynced: Boolean(options?.skipRemote || !sessionToken || isOfflineSessionToken(sessionToken)),
      };
    } finally {
      logoutInFlightRef.current = false;
    }
  }

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handleSessionExpired = async () => {
      if (autoLogoutTriggeredRef.current || logoutInFlightRef.current) {
        return;
      }

      if (!authSession?.isAuthenticated) {
        return;
      }

      autoLogoutTriggeredRef.current = true;

      await logout("inactive", {
        skipRemote: true,
        notice: "Sessao expirada. Autentique-se novamente.",
      });

      redirectToLogin({
        reason: "session-expired",
        notice: "Sessao expirada. Autentique-se novamente.",
      });

      window.dispatchEvent(new CustomEvent(ERP_AUTH_EVENT, {
        detail: {
          action: "session-expired-logout-complete",
          at: Date.now(),
        },
      }));
    };

    window.addEventListener(ERP_SESSION_EXPIRED_EVENT, handleSessionExpired);

    return () => {
      window.removeEventListener(ERP_SESSION_EXPIRED_EVENT, handleSessionExpired);
    };
  }, [authSession?.isAuthenticated, logout]);

  useEffect(() => {
    if (authSession?.isAuthenticated) {
      autoLogoutTriggeredRef.current = false;
    }
  }, [authSession?.isAuthenticated]);

  const clearAuthNotice = () => {
    const nextAuthSession = {
      ...authSession,
      notice: ""
    };

    setAuthSession(nextAuthSession);
    persistAuthSession(nextAuthSession);
  };

  const refreshAuthSession = async ({ forceReloadCompanies = false } = {}) => {
    if (!authSession?.isAuthenticated) {
      return { ok: false, message: "Sessao nao autenticada." };
    }

    if (isOfflineSessionToken(authSession?.token)) {
      return { ok: false, message: "Sessao online indisponivel para sincronizacao." };
    }

    if (refreshAuthInFlightRef.current) {
      return { ok: true, message: "Refresh ja em curso." };
    }

    refreshAuthInFlightRef.current = true;

    const effectiveToken = resolveSessionValidationToken(authSession?.token);

    try {
      // Heartbeat: valida apenas a sess�o � n�o recarrega org data para n�o
      // gerar flood de chamadas a session_bootstrap.php em cada navega��o.
      const sessionUser = await apiValidateSession(effectiveToken);

      // Usa companies em mem�ria; s� recarrega se n�o existirem ainda — exceto
      // quando forceReloadCompanies=true (ex.: logo ap�s concluir o onboarding
      // de uma empresa nova). Sem o force, se `companies` j� tivesse qualquer
      // entrada em mem�ria (de uma empresa anterior/scope antigo), a empresa
      // rec�m-criada nunca entrava na lista e `activeCompany` ficava a apontar
      // para a empresa errada — o ProtectedRoute ficava preso no loader
      // "Aguarde..." � espera de um `activeCompany` que nunca chegava certo.
      const cachedCompanies = (!forceReloadCompanies && companies?.length)
        ? companies
        : await loadOrganizationData(effectiveToken);

      // Esta chamada (apiValidateSession, acima) é assíncrona e pode
      // demorar — sobretudo em rede móvel. Se, enquanto esteve em voo, um
      // logout aconteceu (noutra instância de useErpContext — ver
      // handleLogout em Header.jsx: não há Context React partilhado
      // aqui, cada componente tem o seu próprio estado local), o storage
      // já foi limpo (isAuthenticated:false) antes desta resposta
      // chegar. Sem esta reconfirmação, persistAuthSession() abaixo
      // "ressuscitava" a sessão já terminada — e se a conta tivesse uma
      // troca de senha pendente, o reload de redirectToLogin() apanhava
      // esse estado ressuscitado e ia parar a /alterar-senha em vez de
      // /login (bug confirmado: acontecia só por vezes, só em rede
      // móvel — exactamente o padrão de uma corrida deste tipo).
      if (!readAuthSession()?.isAuthenticated) {
        return { ok: false, message: "Sessao terminada durante a sincronizacao." };
      }

      const sanitizedUser = sanitizeUser(sessionUser, cachedCompanies);
      const nextAuthSession = {
        ...authSession,
        isAuthenticated: true,
        currentUser: sanitizedUser,
        sessionId: sessionUser?.session_id || authSession?.sessionId || null,
        sessionUuid: sessionUser?.session_uuid || authSession?.sessionUuid || null,
        lastLoginAt: authSession?.lastLoginAt || Date.now(),
        notice: ""
      };

      setAuthSession(nextAuthSession);
      persistAuthSession(nextAuthSession);
      updateSnapshot({
        companyId: sanitizedUser?.companyId,
        storeId: sanitizedUser?.storeId,
        roleValue: sanitizedUser?.roleValue,
      }, cachedCompanies);

      return {
        ok: true,
        user: sanitizedUser,
        companies: cachedCompanies,
      };
    } catch (error) {
      return {
        ok: false,
        message: error?.message || "Nao foi possivel sincronizar a sessao.",
      };
    } finally {
      refreshAuthInFlightRef.current = false;
    }
  };

  useEffect(() => {
    if (!authSession?.isAuthenticated || !authSession?.token || isOfflineSessionToken(authSession?.token)) {
      return;
    }

    let active = true;

    const processPendingPasswordChanges = async () => {
      if (passwordSyncInFlightRef.current) {
        return;
      }

      passwordSyncInFlightRef.current = true;
      try {
        const queue = readPendingSyncQueue();
        const pendingItems = (Array.isArray(queue) ? queue : []).filter((item) => item?.type === PASSWORD_CHANGE_SYNC_TYPE);

        if (!pendingItems.length) {
          return;
        }

        const currentUserUuid = String(authSession?.currentUser?.uuid || "").trim();
        const now = Date.now();

        const currentUserItems = pendingItems
          .filter((item) => String(item?.userUuid || "").trim() === currentUserUuid)
          .sort((left, right) => Number(right?.createdAt || 0) - Number(left?.createdAt || 0));

        if (!currentUserItems.length) {
          return;
        }

        // Mant�m apenas a altera��o de senha mais recente por utilizador para evitar conflitos de UX.
        const [latestItem, ...staleItems] = currentUserItems;
        staleItems.forEach((item) => removePendingSyncItem(item?.id));

        const itemsToProcess = [latestItem];

        for (const item of itemsToProcess) {
          if (!active) {
            return;
          }

          const retryAt = Number(item?.nextRetryAt || 0);
          if (Number.isFinite(retryAt) && retryAt > now) {
            continue;
          }

          const payload = item?.payload || {};
          try {
            await apiChangePassword(authSession.token, payload);
            await updateOfflinePasswordCacheForUser({
              userUuid: currentUserUuid,
              email: authSession?.currentUser?.email,
              phone: authSession?.currentUser?.phone,
              newPassword: payload?.new_password,
            });
            removePendingSyncItem(item?.id);
          } catch (error) {
            if (isNetworkUnavailable(error)) {
              return;
            }

            const statusCode = Number(error?.response?.status || 0);
            const attempts = Number(item?.attempts || 0) + 1;

            if (statusCode === 400 || statusCode === 401 || statusCode === 403 || attempts >= 5) {
              removePendingSyncItem(item?.id);
              continue;
            }

            const refreshedQueue = readPendingSyncQueue();
            const nextQueue = (Array.isArray(refreshedQueue) ? refreshedQueue : []).map((queueItem) => {
              if (queueItem?.id !== item?.id) return queueItem;

              const nextDelayMs = Math.min(5 * 60_000, Math.max(20_000, 20_000 * (2 ** Math.min(attempts - 1, 4))));
              return {
                ...queueItem,
                attempts,
                lastErrorAt: Date.now(),
                nextRetryAt: Date.now() + nextDelayMs,
              };
            });
            writePendingSyncQueue(nextQueue);
          }
        }
      } finally {
        passwordSyncInFlightRef.current = false;
      }
    };

    void processPendingPasswordChanges();
    const intervalId = window.setInterval(() => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        return;
      }
      void processPendingPasswordChanges();
    }, 45_000);

    return () => {
      active = false;
      passwordSyncInFlightRef.current = false;
      window.clearInterval(intervalId);
    };
  }, [authSession?.currentUser?.email, authSession?.currentUser?.phone, authSession?.currentUser?.uuid, authSession?.isAuthenticated, authSession?.token]);

  // Drena a fila de vendas/despesas/fecho de caixa pendentes independentemente
  // de o utilizador estar no ecra do POS Terminal ou nao. O efeito equivalente
  // em pos-terminal/index.jsx continua ativo enquanto essa pagina estiver
  // montada (reage mais rapido por estar em primeiro plano); este aqui e a
  // rede de seguranca que garante que nada fica preso a espera de sincronizar
  // so porque o utilizador navegou para outra parte do sistema.
  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    if (!authSession?.isAuthenticated || !authSession?.token || isOfflineSessionToken(authSession?.token) || !activeCompany?.uuid) {
      return undefined;
    }

    let active = true;

    const runGlobalPosOfflineSync = () => {
      if (!active) {
        return;
      }

      void globalPosOfflineSyncRunnerRef.current({
        authToken: authSession.token,
        companyUuid: activeCompany?.uuid,
      });
    };

    runGlobalPosOfflineSync();
    const intervalId = window.setInterval(runGlobalPosOfflineSync, POS_OFFLINE_SYNC_INTERVAL_MS);

    const handleOnline = () => runGlobalPosOfflineSync();
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        runGlobalPosOfflineSync();
      }
    };

    window.addEventListener("online", handleOnline);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      active = false;
      window.clearInterval(intervalId);
      window.removeEventListener("online", handleOnline);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [authSession?.isAuthenticated, authSession?.token, activeCompany?.uuid]);

  // Mesma logica de rede de seguranca, mas para clientes criados/editados
  // offline (utils/customerOfflineSync.js). Ao contrario do catalogo de
  // produtos, este fluxo nao tem revisao de conflitos, por isso e seguro
  // correr em segundo plano sem UI dedicada.
  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    if (!authSession?.isAuthenticated || !authSession?.token || isOfflineSessionToken(authSession?.token) || !activeCompany?.uuid) {
      return undefined;
    }

    let active = true;

    const runGlobalCustomerOfflineSync = () => {
      if (!active) {
        return;
      }

      void globalCustomerOfflineSyncRunnerRef.current({
        authToken: authSession.token,
        companyUuid: activeCompany?.uuid,
        storeUuid: activeStore?.uuid || null,
      });
    };

    runGlobalCustomerOfflineSync();
    const intervalId = window.setInterval(runGlobalCustomerOfflineSync, CUSTOMER_OFFLINE_SYNC_INTERVAL_MS);

    const handleOnline = () => runGlobalCustomerOfflineSync();
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        runGlobalCustomerOfflineSync();
      }
    };

    window.addEventListener("online", handleOnline);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      active = false;
      window.clearInterval(intervalId);
      window.removeEventListener("online", handleOnline);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [authSession?.isAuthenticated, authSession?.token, activeCompany?.uuid, activeStore?.uuid]);

  return {
    activeCompany,
    activeStore,
    availableCompanies: companies,
    activeFiscal,
    activeRole,
    activeTheme,
    availableThemes: ERP_THEMES,
    availableStores,
    isConsolidatedScope,
    isManagerScope,
    canSwitchStore: isManagerScope && hasCrossStoreScope && availableStores?.length > 1,
    scopeUuids,
    matchesActiveScope: (record) => matchesScopeByUuid(record, {
      activeCompany,
      activeStore,
      currentUser: authSession?.currentUser,
    }, companies),
    filterByActiveScope: (records = []) => (records || [])?.filter((record) => matchesScopeByUuid(record, {
      activeCompany,
      activeStore,
      currentUser: authSession?.currentUser,
    }, companies)),
    onlineUsers: activeCompany?.onlineUsers || 0,
    totalUsers: activeCompany?.totalUsers || 0,
    pendingApprovals: activeCompany?.pendingApprovals || 0,
    isAuthenticated: authSession?.isAuthenticated,
    currentUser: authSession?.currentUser,
    authToken: authSession?.token || (authSession?.isAuthenticated ? COOKIE_ONLY_AUTH_TOKEN_MARKER : ""),
    authNotice: authSession?.notice,
    lastLoginAt: authSession?.lastLoginAt,
    canViewModule: (moduleKey) => accessibleModules.has(String(moduleKey || "").trim()),
    canAccessAction: (actionKey) => hasAccess(ACTION_PERMISSIONS?.[actionKey], activeRole?.value, isConsolidatedScope),
    canAccessRoute: (pathname) => {
      const moduleKey = resolveModuleKeyForPath(pathname);
      if (!moduleKey) {
        return true;
      }

      return accessibleModules.has(moduleKey);
    },
    login,
    loginWithPin,
    setLoginPin,
    removeLoginPin,
    logout,
    redirectToLogin,
    clearAuthNotice,
    acknowledgeLocalPasswordChange,
    refreshAuthSession,
    setActiveCompany: async (companyId) => {
      if (!hasCrossStoreScope) {
        return { ok: false, reason: "store-scope-locked", message: "Perfil atual nao pode trocar de empresa." };
      }

      const company = getCompanyById(companyId, companies);
      const nextStore = company?.stores?.find((store) => store?.principal) || company?.stores?.[0];

      if (!company || !nextStore || !authSession?.token) {
        updateSnapshot({ companyId, storeId: nextStore?.id }, companies);
        return { ok: true };
      }

      try {
        const result = await apiSelectUserStore(authSession?.token, { loja_uuid: nextStore?.uuid });
        setAuthenticatedUser(result?.user, {
          token: authSession?.token,
          sessionId: result?.user?.session_id || authSession?.sessionId,
          sessionUuid: result?.user?.session_uuid || authSession?.sessionUuid,
        }, "", companies);
        return { ok: true, user: result?.user };
      } catch (error) {
        if (String(error?.apiCode || "").trim().toUpperCase() === "STORE_LICENSE_EXPIRED") {
          return {
            ok: false,
            reason: "store-license-expired",
            message: error?.message || "A licenca da loja selecionada expirou.",
            data: error?.apiData || null,
          };
        }

        return { ok: false, message: error?.message || 'Nao foi possivel mudar a empresa.' };
      }
    },
    setActiveStore: async (storeId) => {
      if (!hasCrossStoreScope) {
        return { ok: false, reason: "store-scope-locked", message: "Perfil atual nao pode trocar de loja." };
      }

      const nextStore = availableStores?.find((store) => store?.id === storeId);

      if (!nextStore || !authSession?.token) {
        updateSnapshot({ storeId }, companies);
        return { ok: true };
      }

      try {
        const result = await apiSelectUserStore(authSession?.token, { loja_uuid: nextStore?.uuid });
        setAuthenticatedUser(result?.user, {
          token: authSession?.token,
          sessionId: result?.user?.session_id || authSession?.sessionId,
          sessionUuid: result?.user?.session_uuid || authSession?.sessionUuid,
        }, "", companies);
        return { ok: true, user: result?.user };
      } catch (error) {
        if (String(error?.apiCode || "").trim().toUpperCase() === "STORE_LICENSE_EXPIRED") {
          return {
            ok: false,
            reason: "store-license-expired",
            message: error?.message || 'A licenca da loja selecionada expirou.',
            data: error?.apiData || null,
          };
        }

        return { ok: false, message: error?.message || 'Nao foi possivel mudar a loja.' };
      }
    },
    setActiveFiscal: (fiscalValue) => updateSnapshot({ fiscalValue }),
    setActiveRole: (roleValue) => {
      if (authSession?.isAuthenticated && authSession?.currentUser?.roleValue) return;
      updateSnapshot({ roleValue });
    },
    setActiveTheme: (themeValue) => {
      const nextThemeValue = getThemeByValue(themeValue)?.value || ERP_THEMES?.[0]?.value;

      if (typeof document !== "undefined") {
        const root = document.documentElement;
        root.dataset.theme = nextThemeValue;
        root.classList.toggle("dark", nextThemeValue === "midnight");
      }

      updateSnapshot({ themeValue: nextThemeValue });
    }
  };
}


